export const en = {
  // Common
  common: {
    envBannerPrefix: 'You are on',
    envBannerUnverified: 'Unverified environment',
    versionBadgeTitle: 'View release notes',
    versionBadgeDev: 'dev',
    ok: 'OK',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    readOnly: 'Read-only',
    none: 'None',
    change: 'Change',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    submit: 'Submit',
    go: 'Go',
    reset: 'Reset',
    confirm: 'Confirm',
    verify: 'Verify',
    close: 'Close',
    clickToViewDetails: 'Click to view details',
    done: 'Done',
    cancelled: 'Cancelled',
    print: 'Print',
    back: 'Back',
    goBack: 'Go Back',
    dismiss: 'Dismiss',
    dismissed: 'dismissed',
    noResults: 'No results found',
    transfer: 'Transfer',
    session: 'Session',
    next: 'Next',
    previous: 'Previous',
    loading: 'Loading...',
    processing: 'Processing...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Information',
    yes: 'Yes',
    no: 'No',
    select: 'Select',
    selectAll: 'Select All',
    actions: 'Actions',
    status: 'Status',
    station: 'Station',
    active: 'Active',
    inactive: 'Inactive',
    enabled: 'Enabled',
    disabled: 'Disabled',
    total: 'Total',
    of: 'of',
    showing: 'Showing',
    logout: 'Logout',
    settings: 'Settings',
    location: 'Location',
    lot: 'Lot',
    lotNumber: 'Lot Number',
    case: 'case',
    cases: 'cases',
    selectLot: 'Select lot...',
    selectProductFirst: 'Select a product first...',
    transferNumber: 'Transfer Number',
    saving: 'Saving',
    deleting: 'Deleting',
    view: 'View',
    viewAll: 'View All',
    optional: 'Optional',
    accept: 'Accept',
    refresh: 'Refresh',
    moreActions: 'More actions',
    linked: 'Linked',
    records: 'records',
    all: 'All',
    continue: 'Continue',
    filters: 'Filters',
    exportCsv: 'Export CSV',
    clear: 'Clear',
    packer: 'Packer',
    startedAt: 'Started At',
    completedAt: 'Completed At',
  },

  // Print / Label Preview
  print: {
    previewTitle: 'Label Preview',
    previewDescription: 'This is what the label would look like when printed.',
    copy: 'copy',
    copies: 'copies',
    willBePrinted: 'will be printed',
  },

  // Status labels for sessions (WCO-2146)
  status: {
    pending: 'Pending',
    submitted: 'Submitted',
    received: 'Received',
    ready: 'Ready',
    active: 'Active',
    paused: 'Paused',
    completed: 'Completed',
    cancelled: 'Cancelled',
    // Legacy alias for pre-WCO-2146 'planned' rows
    planned: 'Received',
  },

  // OAuth - WayFong domain authentication
  oauth: {
    validating: 'Validating organization access...',
    redirecting: 'Redirecting to organization login...',
    error: 'Organization authentication failed',
    retry: 'Try Again',
    sessionExpired: 'Your organization session has expired.',
    loggedInAs: 'Logged in as',
    accessDenied: {
      title: 'No access to HTE Production',
      message: "Your account isn't authorized to use HTE Production. Contact your manager to request access.",
      signOut: 'Sign out',
    },
    signedOut: {
      title: 'Signed out',
      message: 'You have been signed out of HTE Production.',
      signIn: 'Sign in',
    },
  },

  // Authentication
  auth: {
    login: 'Login',
    username: 'Username',
    password: 'Password',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    loginButton: 'Sign In',
    loginError: 'Invalid username or password',
    sessionExpired: 'Your session has expired. Please login again.',
    unauthorized: 'You are not authorized to access this resource',
    // Password confirmation modal (WCO-2706)
    loggedInAs: 'Logged in as',
  },

  // Navigation
  nav: {
    clockIn: 'Clock In',
    dashboard: 'Dashboard',
    users: 'Users',
    locations: 'Locations',
    inventory: 'Inventory',
    stations: 'Stations',
    settings: 'Settings',
    supervisor: 'Dashboard',
    operator: 'Pool To Do',
    production: 'Production',
    productionEntry: 'Production Entry',
    mobileView: 'Mobile View',
    desktopView: 'Desktop View',
    reports: 'Reports',
    productCatalog: 'Item Catalog',
    itemCatalog: 'Item Catalog',
    cameraManagement: 'Camera Management',
    cameraMonitoringDashboard: 'Camera Monitoring Dashboard',
    labelManagement: 'Label Management',
    transferVerification: 'Receiving',
    transferHistory: 'Transfer History',
    productionAssignments: 'Production Assignments',
    materialConsumption: 'Material Consumption',
    dailyGuide: 'Production Guide',
    managerDashboard: 'Dashboard',
    endOfDaySummary: 'End of Day Summary',
    overrideHistory: 'Override History',
    adminActivity: 'Admin Activity',
    warehouseTransfer: 'Transfers',
    approvalQueue: 'Approval Queue',
    boxSearch: 'Box Search',
    yieldVariance: 'Yield Variance',
    approvalHistory: 'Approval History',
    itemSummaryReport: 'Item Summary',
    logout: 'Logout',
    language: 'Language',
    dailyWorkQueue: 'Daily Work Queue',
    workSchedule: 'Work Schedule',
    schedule: 'Schedule',
    byproductMapping: 'Product Mapping',
    productionRecords: 'Production Records',
    productionProgress: 'Production Progress',
    devTools: 'Dev Tools',
    tutorial: 'Tutorial',
    notifications: 'Notifications',
    noNotifications: 'No notifications',
  },

  // Schedule page (combined view)
  schedule: {
    weeklyView: 'Weekly View',
    dailyQueue: 'Daily Queue',
  },

  dailyWorkQueue: {
    title: 'Daily Work Queue',
    subtitle: 'View and track daily production sub-sessions',
    status: 'Status',
    station: 'Station',
    operator: 'Operator',
    packer: 'Packer',
    product: 'Product',
    expectedBoxes: 'Expected Boxes',
    producedBoxes: 'Produced Boxes',
    scheduledDate: 'Schedule Date',
    subSession: 'Sub-Session #',
    sessionNumber: 'Session #',
    noData: 'No sub-sessions found for the selected date range.',
    unassigned: 'Unassigned',
    today: 'Today',
    allStatuses: 'All Statuses',
    scheduledWorkforce: 'Scheduled Workforce',
    operators: 'Operators',
    packers: 'Packers',
    stations: 'Stations',
    availableOf: 'available',
    noStation: 'No station',
    assigned: 'Assigned',
    scheduledStations: 'Scheduled for today',
    clickToAssign: 'Assign',
    assignStation: 'Assign Station',
    assignOperator: 'Assign Operator',
    assignPacker: 'Assign Packer',
    selectStation: 'Select a station',
    selectOperator: 'Select an operator',
    selectPacker: 'Select a packer',
    assignedSuccess: 'Assigned successfully',
    noAvailable: 'No available options',
    clickToReassign: 'Click to reassign',
    reassignStation: 'Reassign Station',
    reassignOperator: 'Reassign Operator',
    reassignPacker: 'Reassign Packer',
    currentAssignee: 'Current',
  },

  // Manager
  manager: {
    dashboard: 'Dashboard',
    dashboardSubtitle: 'Monitor production and manage sessions',
    vb6TransferReminders: 'Pending VB6 Transfer Deletions',
    vb6TransferRemindersDesc: 'The following cancelled sessions have submitted transfers that need to be deleted in the VB6 system.',
    transferReminderDismissed: 'Reminder dismissed',
    transferReminderResolved: 'Transfer confirmed deleted',
    markResolved: 'Resolve',
    excessiveReprintsAlert: 'Excessive label reprints detected',
    reprints: 'reprints',
    reprintAlertResolved: 'Reprint alert resolved',
    draftTransferReminder: 'Transfer to HTE not yet submitted',
    todaysSessions: "Today's Sessions",
    plannedSessions: 'Submitted',
    activeSessions: 'Active',
    completedSessions: 'Completed',
    quickActions: 'Quick Actions',
    manageSessionsDesc: 'Create and manage production sessions',
    stationsActive: 'active',
    viewInventoryDesc: 'View materials and inventory',
    todaysWorkforce: "Today's Workforce",
    operators: 'Operators',
    packers: 'Packers',
    stations: 'Stations',
    remainingMaterialLots: 'Material Lots',
    lots: 'Lots',
    weight: 'Weight',
    sessions: 'Sessions',
    stationOverview: 'Station Overview',
    sessionOverview: 'Session Overview',
    stationView: 'Station View',
    sessionView: 'Session View',
    productionProgressOverview: 'Production Progress Overview',
    overall: 'Overall',
    noSessionsToday: 'No production sessions scheduled for today',
    operator: 'Operator',
    packer: 'Packer',
    boxes: 'Boxes',
    lbs: 'lbs',
    noActiveSession: 'No active session',
    todaysOutput: "Today's Output",
    input: 'Input',
    output: 'Output',
    avgYield: 'Avg Yield',
    expected: 'Expected',
    completion: 'Completion',
    // KPI detail modal
    kpiDetail: {
      sessionsTitle: "Today's Sessions Breakdown",
      workforceTitle: "Today's Workforce Breakdown",
      lotsTitle: 'Material Lots Breakdown',
      outputTitle: "Today's Output Breakdown",
      sessionNumber: 'Session #',
      product: 'Product',
      status: 'Status',
      subSessions: 'Sub-Sessions',
      submitted: 'Submitted',
      received: 'Received',
      planned: 'Received',
      active: 'Active',
      completed: 'Completed',
      employeeNumber: 'Employee #',
      name: 'Name',
      role: 'Role',
      stationName: 'Station',
      type: 'Type',
      itemNumber: 'Item #',
      itemName: 'Item Name',
      weightLbs: 'Weight (lbs)',
      location: 'Location',
      receivedDate: 'Received',
      outputBoxes: 'Output (CS)',
      expectedBoxes: 'Expected (CS)',
      completionPct: 'Completion %',
      noData: 'No data available',
      clickToViewDetails: 'Click to view details',
      loading: 'Loading...',
      operatorsSection: 'Operators',
      packersSection: 'Packers',
      stationsSection: 'Stations',
      rowNumber: '#',
      cases: 'Cases',
    },
  },

  // Transfer History
  transferHistory: {
    subtitle: 'View history of verified transfer receipts',
  },

  productionProgress: {
    activeStations: 'Active Stations',
    boxesPacked: 'Boxes Packed',
    totalWeight: 'Total Weight',
    avgOutput: 'Avg Output',
    all: 'All',
    station: 'Station',
    queue: 'Queue',
    product: 'Product',
    operator: 'Operator',
    packer: 'Packer',
    status_header: 'Status',
    boxes: 'Boxes',
    progress: 'Progress',
    weight: 'Weight (lbs)',
    elapsed: 'Elapsed',
    noSessions: 'No production sessions for this date',
    status: {
      active: 'Active',
      paused: 'Paused',
      ready: 'Ready',
      received: 'Received',
      submitted: 'Submitted',
      planned: 'Received',
      pending: 'Pending',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
  },

  productionRecords: {
    subtitle: 'View ICS production records and HTE staging data',
    source: 'Source',
    syncStatus: 'Sync Status',
    pending: 'Pending Sync',
    synced: 'Synced',
    failed: 'Failed',
    allSources: 'All Sources',
    inputMaterials: 'Input Materials',
    outputProducts: 'Output Products',
    weightEntries: 'weight entries',
    noRecords: 'No production records found',
    sessionLink: 'View Session',
    detailCount: 'Detail Lines',
    productionNumber: 'Production #',
    dateRange: 'Date Range',
    operator: 'Operator',
    location: 'Location',
    createdBy: 'Created By',
    // Detail view
    title: 'Production Record',
    productionNo: 'Production No.',
    printBarcodes: 'Print Barcodes',
    printBarcodesPopupBlocked: 'Pop-up blocked — allow pop-ups to print barcodes',
    printBarcodesFailed: 'Could not load barcodes',
    requestDate: 'Request Date',
    finishDate: 'Finish Date',
    productionComplete: 'Production Complete',
    productionLocation: 'Production Location',
    memo: 'Memo',
    inputTab: 'Input Production Material',
    outputTab: 'Output Finish Good',
    quantity: 'Req. Qty',
    quantityShipped: 'Ship Qty',
    weightShipped: 'Weight',
    warehouseCol: 'Warehouse',
    locationCol: 'Loc',
    referenceNo: 'Reference No.',
    unitPrice: 'Unit Price',
    amount: 'Amount',
    materialDocNo: 'M. Doc #',
    materialDocType: 'M. Doc Type',
    eItem: 'E-Item',
    backToList: 'Back to List',
    notFound: 'Production record not found',
    loadFailed: 'Failed to load production record',
  },

  // Dashboard
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome',
    overview: 'System Overview',
    activeUsers: 'Active Users',
    activeStations: 'Active Stations',
    todayProduction: "Today's Production",
    alerts: 'Alerts',
    recentActivity: 'Recent Activity',
    statistics: 'Statistics',
    performance: 'Performance',
  },

  // Users
  users: {
    title: 'User Management',
    addUser: 'Add User',
    editUser: 'Edit User',
    deleteUser: 'Delete User',
    userDetails: 'User Details',
    userId: 'User ID',
    fullName: 'Full Name',
    userLevel: 'User Level',
    location: 'Location',
    department: 'Department',
    email: 'Email',
    phone: 'Phone',
    createdAt: 'Created At',
    lastLogin: 'Last Login',
    permissions: 'Permissions',
    role: 'Role',
    admin: 'Admin',
    supervisor: 'Supervisor',
    operator: 'Operator',
    userCount: 'Total Users',
    activeCount: 'Active Users',
    searchUsers: 'Search users...',
  },

  // Locations
  locations: {
    title: 'Location Management',
    addLocation: 'Add Location',
    editLocation: 'Edit Location',
    deleteLocation: 'Delete Location',
    locationDetails: 'Location Details',
    locationId: 'Location ID',
    locationCode: 'Location Code',
    locationName: 'Location Name',
    description: 'Description',
    address: 'Address',
    manager: 'Manager',
    capacity: 'Capacity',
    stationCount: 'Stations',
    searchLocations: 'Search locations...',
  },

  // Stations
  stations: {
    title: 'Station Management',
    addStation: 'Add Station',
    editStation: 'Edit Station',
    deleteStation: 'Delete Station',
    stationDetails: 'Station Details',
    stationId: 'Station ID',
    stationName: 'Station Name',
    stationType: 'Station Type',
    location: 'Location',
    assignedOperator: 'Assigned Operator',
    currentProduction: 'Current Production',
    targetProduction: 'Target Production',
    efficiency: 'Efficiency',
    searchStations: 'Search stations...',
    printerMac: 'Printer MAC Address',
    printerMacDesc: 'MAC address of the Zebra printer assigned to this station',
    printerStatus: 'Printer Status',
    printerConnected: 'Connected',
    printerNotFound: 'Not Found',
    printerNotAssigned: 'No Printer Assigned',
    assignPrinter: 'Assign Printer',
    removePrinter: 'Remove Printer',
    discoverPrinters: 'Discover Printers',
    testPrint: 'Test Print',
    testPrintSuccess: 'Test label sent to printer successfully',
    testPrintFailed: 'Test print failed',
    testPrintPrinterName: 'Printer: {{name}}',
    printerMacPlaceholder: 'XX:XX:XX:XX:XX:XX',
    discovering: 'Discovering...',
    noPrintersFound: 'No printers found on the network',
    selectPrinter: 'Select a printer to assign',
    printerAssigned: 'Printer assigned successfully',
    printerRemoved: 'Printer removed',
    printerLinkError: 'Failed to save printer assignment — station may not have an assigned printer',
  },

  // Inventory
  inventory: {
    title: 'Inventory Management',
    dashboard: 'Inventory Dashboard',
    productCatalog: 'Item Catalog',
    itemCatalog: 'Item Catalog',
    browseManageItems: 'Browse and manage all inventory items',
    addNewItem: 'Add New Item',
    itemType: 'Item Type',
    allItems: 'All Items',
    rawMaterials: 'Raw Materials',
    finishedProducts: 'Finished Products',
    searchItems: 'Search Items',
    searchByItemNumber: 'Search by Item Number or Name...',
    allLocations: 'All Locations',
    itemName: 'Item Name',
    itemNumber: 'Item #',
    stock: 'Stock',
    outOfStock: 'Out of Stock',
    lowStock: 'Low Stock',
    inStock: 'In Stock',
    viewDetails: 'View Details',
    loadingItems: 'Loading items...',
    failedToLoadItems: 'Failed to load items',
    noItemsFound: 'No items found',
    finishedGoods: 'Finished Goods',
    stockLevel: 'Stock Level',
    reorderPoint: 'Reorder Point',
    supplier: 'Supplier',
    category: 'Category',
    sku: 'SKU',
    barcode: 'Barcode',
    quantity: 'Qty',
    unit: 'Unit',
    weight: 'Weight',
    price: 'Price',
    cost: 'Cost',
    lastUpdated: 'Last Updated',
    updatedAt: 'Updated At',
    updatedBy: 'Updated By',
    searchInventory: 'Search inventory...',
    // Lot inventory
    availableLots: 'Available Lots',
    expiringSoon: 'Expiring Soon',
    within7Days: 'Within 7 days',
    packedBoxes: 'Packed Boxes',
    onHandFinished: 'On-Hand Finished',
    dtoShipped: 'DTO shipped',
    onHandCases: 'On Hand (Cases)',
    onHandCasesNote: 'Packed cases − DTO shipped (item-level)',
    onHandWeight: 'On-Hand Weight',
    fpNiOnHand: 'FP-NI On Hand',
    includesFpNi: 'Includes FP-NI',
    finishedProductsReconciliationNote: 'Shipped quantities are derived from Daily Transfer Out (item-level aggregate; not box-exact). On-hand cases = packed production inventory minus DTO finished-product shipments.',
    boxesToday: 'Boxes Today',
    shipped: 'shipped',
    rawMaterialLots: 'Raw Material Lots',
    allStatuses: 'All Statuses',
    statusAvailable: 'Available',
    statusReserved: 'Reserved',
    statusDepleted: 'Depleted',
    statusPacked: 'Packed',
    statusShipped: 'Shipped',
    statusVoided: 'Voided',
    statusRejected: 'Rejected',
    statusOn_hold: 'On Hold',
    statusConsumed: 'Consumed',
    statusRemainder: 'Remainder',
    statusByProduct: 'By-Product',
    statusByProductM: 'By-Product (Marketable)',
    statusByProductP: 'By-Product (Production)',
    statusFinishProductNi: 'Finish Product - Not Intact',
    lotType: 'Type',
    lotTypeRaw: 'Intact',
    lotTypeRemainder: 'Intact',
    lotTypeRemainderNi: 'Not Intact',
    lotTypeFpNi: 'FP-NI',
    lotTypeByProductP: 'By-Product — Production',
    lotTypeByProductM: 'By-Product — Marketable',
    // Filter-dropdown labels — filter by FORM (WCO-3270); distinct keys from the per-row badge keys above
    lotTypeIntact: 'Intact',
    lotTypeNotIntact: 'Not Intact',
    allLotTypes: 'All Types',
    allLifecycleStatuses: 'All Statuses',
    marketableByproduct: 'Marketable Byproduct',
    onHoldApproved: 'On hold (approved)',
    onHoldPending: 'On hold (pending)',
    lotNumber: 'Lot Number',
    currentWeight: 'Current Wgt',
    allocatedToSession: 'Allocated to Session',
    expiryDate: 'Expiry Date',
    receivedDate: 'Received Date',
    receivedBy: 'Received By',
    productName: 'Product Name',
    packedAt: 'Packed At',
    boxCount: 'Case Count',
    boxCountNote: 'Includes cancelled cases',
    expectedCount: 'Expected',
    totalWeight: 'Total Weight',
    lastPacked: 'Last Packed',
    yieldFactor: 'Yield Factor',
    // Raw Material Receiving
    addRawMaterial: 'Add Raw Material',
    receiveRawMaterial: 'Receive Raw Material',
    scanTransferNumber: 'Scan Transfer Number',
    enterTransferNumber: 'Enter or scan transfer number...',
    transferInfo: 'Transfer Information',
    transferNumber: 'Transfer Number',
    transferDate: 'Transfer Date',
    fromLocation: 'From Location',
    toLocation: 'To Location',
    noTransferFound: 'No transfer found with this number',
    transferNotFound: 'Transfer Not Found',
    startScanningBoxes: 'Start Scanning Boxes',
    scanBoxes: 'Scan Boxes',
    scanBoxBarcode: 'Scan box barcode...',
    boxesScanned: 'Boxes Scanned',
    removeBox: 'Remove',
    confirmReceiving: 'Confirm Receiving',
    confirmReceivingMessage: 'You are about to receive the following raw material:',
    receivingSuccess: 'Raw material received successfully',
    receivingError: 'Failed to receive raw material',
    invalidTransfer: 'Invalid transfer number',
    selectExpiryDate: 'Select expiry date',
    boxes: 'Boxes',
    lbs: 'lbs',
    back: 'Back',
    confirmAndSave: 'Confirm & Save',
    scanningBoxesFor: 'Scanning boxes for',
    noBoxesScanned: 'No boxes scanned yet',
    scanBoxToAdd: 'Scan a box barcode to add it to the list',
    itemsInTransfer: 'Items in Transfer',
    expectedQuantity: 'Expected Qty',
    expectedWeight: 'Expected Weight',
    productNotInTransfer: 'Product not found in this transfer',
    invalidBarcode: 'Invalid barcode format',
    alreadyReceived: 'Received',
    pending: 'Pending',
    itemAlreadyReceived: 'This item has already been received from this transfer',
    // Box Lookup
    boxLookup: 'Box Lookup',
    boxLookupSearchPlaceholder: 'Enter or scan barcode...',
    boxLookupSearch: 'Search',
    boxLookupNoResults: 'No box found with this barcode',
    boxLookupError: 'Failed to look up barcode',
    boxLookupProductSku: 'Item Number',
    boxLookupRawMaterial: 'Raw Material',
    boxLookupBoxNumber: 'Box Number',
    boxLookupWeight: 'Weight',
    boxLookupStation: 'Station',
    boxLookupOperator: 'Operator',
    boxLookupPacker: 'Packer',
    boxLookupEmployeeNumber: 'Employee #',
    boxLookupSession: 'Session',
    boxLookupLot: 'Lot Number',
    boxLookupPackedAt: 'Packed At',
    boxLookupPrintedAt: 'Label Printed At',
    boxLookupPackedAndLabeledAt: 'Packed & Labeled At',
    boxLookupStatus: 'Status',
    boxLookupBarcode: 'Barcode',
    boxLookupNotAvailable: 'N/A',
    // Marketable By-Product (buffer / case) Lookup
    bufferFound: 'Marketable By-Product (Buffer)',
    caseFound: 'Marketable By-Product (Case)',
    bufferSourceMaterial: 'Source Material',
    bufferReservedWeight: 'Weight',
    bufferSubSession: 'Sub-Session',
    bufferProductionDate: 'Production Date',
    // Raw Material Lookup
    rawMaterialFound: 'Raw Material',
    rawMaterialItemNumber: 'Item Number',
    rawMaterialGtin: 'GTIN',
    rawMaterialLot: 'Lot Number',
    rawMaterialWeight: 'Scanned Weight',
    rawMaterialBrand: 'Brand',
    rawMaterialYieldFactor: 'Yield Factor',
    // Transaction History tab
    transactionHistory: 'Transaction History',
    transactionType: 'Type',
    allTypes: 'All Types',
    change: 'Change',
    balance: 'Balance',
    noTransactions: 'No transactions found',
    transactionSessionStart: 'Session Start',
    transactionSessionCancel: 'Session Cancel',
    transactionSessionComplete: 'Session Complete',
    transactionCasePacked: 'Case Packed',
    transactionCaseCancelled: 'Case Cancelled',
    transactionCaseVoided: 'Case Voided',
    transactionCasePending: 'Case Pending',
    transactionWasteRecorded: 'Waste Recorded',
    transactionManualAdjustment: 'Manual Adjustment',
    transactionOracleSync: 'Oracle Sync',
    transactionMaterialIssue: 'Material Issue',
    transactionLotReceived: 'Lot Received',
    transactionLotRejected: 'Lot Rejected',
    transactionLotReserved: 'Lot Reserved',
    transactionByProductReceived: 'By-Product',
    transactionByProductMarketableReceived: 'By-Product (Marketable)',
    transactionByProductProductionReceived: 'By-Product (Production)',
    transactionRemainderReceived: 'Remainder',
    transactionRemainderIntactReceived: 'Remainder (Intact)',
    transactionRemainderNotIntactReceived: 'Remainder (Not Intact)',
    transactionByProductMarketablePrinted: 'By-Product Marketable Label Printed',
    transactionByProductCasePrinted: 'By-Product Case Label Printed',
    transactionFinishProductNotIntactReserved: 'Finish Product NI Reserved for Transfer',
    transactionFinishProductNotIntactReceived: 'Finish Product NI Received',

    scanDetails: 'Scan Details',
    box: 'Box',
    noScanDetails: 'No scan details available for this lot',
    remark: 'Remark',
    scannedAt: 'Scanned At',

    // Packing Materials tab
    packingMaterials: 'Packing Materials',
    totalPackingItems: 'Packing Items',
    totalPackingLots: 'Packing Lots',
    lowStockItems: 'Low/Zero Stock',
    linkedProducts: 'Linked Products',
  },

  // Production
  production: {
    title: 'Production',
    assignments: 'Production Assignments',
    schedule: 'Production Schedule',
    batch: 'Batch',
    batchNumber: 'Batch Number',
    startTime: 'Start Time',
    endTime: 'End Time',
    duration: 'Duration',
    producedQuantity: 'Produced Quantity',
    targetQuantity: 'Target Quantity',
    progress: 'Progress',
    materials: 'Materials',
    consumption: 'Material Consumption',
    yield: 'Yield',
    expectedYield: 'Expected Yield',
    actualYield: 'Actual Yield',
    waste: 'Waste',
    notes: 'Notes',
    remnantAvailable: 'Remnant available',
  },

  // Settings
  settings: {
    title: 'Settings',
    general: 'General Settings',
    system: 'System Settings',
    appearance: 'Appearance',
    language: 'Language',
    theme: 'Theme',
    notifications: 'Notifications',
    security: 'Security',
    backupRestore: 'Backup & Restore',
    about: 'About',
    version: 'Version',
  },

  // Validation
  validation: {
    required: 'This field is required',
    invalidEmail: 'Invalid email address',
    invalidPhone: 'Invalid phone number',
    minLength: 'Minimum length is {{min}} characters',
    maxLength: 'Maximum length is {{max}} characters',
    passwordMismatch: 'Passwords do not match',
    invalidFormat: 'Invalid format',
    mustBeNumber: 'Must be a number',
    mustBePositive: 'Must be a positive number',
  },

  // Messages
  messages: {
    saveSuccess: 'Saved successfully',
    deleteSuccess: 'Deleted successfully',
    updateSuccess: 'Updated successfully',
    createSuccess: 'Created successfully',
    deleteConfirm: 'Are you sure you want to delete this item?',
    unsavedChanges: 'You have unsaved changes. Do you want to leave?',
    operationFailed: 'Operation failed',
    networkError: 'Network error. Please check your connection.',
    serverError: 'Server error. Please try again later.',
    loadFailed: 'Failed to load {item}. Some features may use defaults.',
    refreshFailed: 'Failed to refresh data. Please reload the page.',
    noData: 'No data available',
    noResults: 'No results found',
  },

  // Date & Time
  dateTime: {
    date: 'Date',
    time: 'Time',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    lastWeek: 'Last Week',
    lastMonth: 'Last Month',
    customRange: 'Custom Range',
    from: 'From',
    to: 'To',
  },

  // Operator
  operator: {
    // Production Entry
    packBoxes: 'Pack Boxes',
    stationAssigned: 'Station Assigned',
    productionSessionAssigned: 'Production session assigned to your station',
    noStationAssignment: 'No Station Assignment',
    noSessionAssigned: 'You currently have no production session assigned to your station.',
    contactSupervisor: 'Please contact your supervisor to assign a production session.',
    enterPackageWeight: 'Enter Package Weight',
    printAndPackBox: 'Print & Pack Box',
    printAndPackSubUnit: 'Print & Pack Sub-unit',
    packSubUnit: 'Pack Sub-Unit',
    subUnit: 'Sub-unit',
    masterBox: 'Master Box',
    weighMasterBoxPrompt: 'All sub-units weighed. Now weigh the master box.',
    nowWeighTheBox: 'Now weigh the box with all sub-units inside',
    continue: 'Continue',
    subUnitWeightMissing: 'Product data incomplete - sub_unit_weight not set. Contact supervisor.',
    subUnitPacked: 'Sub-unit packed!',
    printing: 'Printing...',
    target: 'Target',
    scaleReading: 'Scale Reading',
    readyForWeighing: 'Ready for weighing',
    acceptable: 'Acceptable',
    productionPausedResume: 'Production Paused - Click "Resume" to continue',
    clickStartProduction: 'Click "Start" to begin production',

    // Scale Weight component
    targetWeightScale: 'Target Weight',
    tareButton: 'Tare/Zero Scale (Reset for Package Weight)',
    targetReachedMsg: 'Target reached - No more packing allowed',
    targetUnreachedMsg: 'Place empty package on scale and click to zero it out',
    weightAcceptable: 'Weight is acceptable',
    invalidWeightNegative: 'Invalid weight (negative)',
    overTarget: 'OVER target',
    underTarget: 'UNDER target',
    acceptableRangeLabel: 'Acceptable',
    removeContentsReweigh: 'Remove contents and re-weigh',
    addContentsReweigh: 'Add contents and re-weigh',

    // Metrics
    boxesPacked: 'Boxes Packed',
    estimated: 'Estimated',
    totalOutput: 'Total Output',
    outputProgress: 'Output Progress',
    recentBoxes: 'Recent Boxes',
    boxNumber: 'Box #',
    weight: 'Weight',
    time: 'Time',
    tolerance: 'Tolerance',

    // Session Info
    sessionInfo: 'Session Info',
    product: 'Product',
    itemNumber: 'Item Number',
    station: 'Station',
    lot: 'Lot',
    supervisor: 'Supervisor',
    initialMaterial: 'Initial Material',
    usableMaterial: 'Usable Material',
    rawMaterial: 'Raw Material',
    targetWeight: 'Target Weight/Box',
    targetOutput: 'Target',

    // Session Time & Material Details
    sessionTime: 'Session Time',
    startedAt: 'Started',
    materialDetails: 'Material Details',
    initialMaterialWeight: 'Initial Material Weight',
    targetOutputWeight: 'Target Output Weight',
    actualOutputWeight: 'Actual Output Weight',
    yieldRate: 'Yield Rate',
    yield: 'Yield',
    completionRate: 'Completion Rate',
    weightModeChanged: 'Weight mode changed to',

    // Quality Control
    qualityControlCheck: 'Quality Control Check',
    completeAllChecks: 'Complete all quality checks before packing box',
    visualInspection: 'Visual Inspection',
    weightVerified: 'Weight Verified',
    labelingCorrect: 'Labeling Correct',
    packagingIntact: 'Packaging Intact',
    checkAll: 'Check All',
    uncheckAll: 'Uncheck All',
    qualityChecksCompleted: 'Quality Checks Completed',

    // Quality descriptions
    qualityDescVisual: 'Product appears fresh, no discoloration or damage',
    qualityDescWeight: 'Weight is',
    qualityDescLabeling: 'Label printed clearly with correct lot number and weight',
    qualityDescPackaging: 'No tears, punctures, or defects in packaging',

    // Packing Confirmation
    confirmPacking: 'Confirm Packing',
    labelPrinted: 'Label has been printed successfully',
    verifyBeforePacking: 'Please verify the following before confirming',
    boxWeight: 'Box Weight',
    withinTolerance: 'Within Tolerance',
    allQualityChecksPassed: 'All quality checks passed',
    confirmAndPack: 'Confirm & Pack',

    // Label Applied Confirmation
    confirmLabelApplied: 'Confirm Label Applied',
    labelsPrinted: '{{count}} label copies have been printed',
    confirmLabelAppliedDescription: 'Please confirm that the label has been applied correctly to the box before proceeding.',

    // Shipping/Transfer
    ship: 'Ready to Transfer/Pickup',
    shipped: 'Shipped',
    selectShippingLocation: 'Select the destination location for shipping this box.',
    shippingLocationRequired: 'Select shipping location (required)...',
    confirmShip: 'Confirm Ship',
    shipping: 'Shipping...',
    shippedTo: 'To',

    // Locations
    warehouseA: 'Warehouse A',
    warehouseB: 'Warehouse B',
    warehouseC: 'Warehouse C',
    distributionCenter1: 'Distribution Center 1',
    distributionCenter2: 'Distribution Center 2',
    coldStorage: 'Cold Storage',
    retailStore: 'Retail Store',
    customerDirect: 'Customer Direct',

    // Session Control
    start: 'Start',
    pause: 'Pause',
    resume: 'Resume',
    complete: 'Complete',
    startSession: 'Start Session',
    pauseSession: 'Pause Session',
    resumeSession: 'Resume Session',
    completeSession: 'Complete Session',
    starting: 'Starting...',
    pausing: 'Pausing...',
    resuming: 'Resuming...',
    completing: 'Completing...',
    pauseProductionSession: 'Pause Production Session',
    reviewProgressAndReason: 'Review current progress and provide a reason for pausing the session.',
    selectPauseReason: 'Select pause reason (required)...',
    selectCompletionReason: 'Select completion reason...',
    addFinalNotes: 'Add any final notes or observations (optional)...',

    // Status Messages
    sessionStarted: 'Session started successfully',
    sessionPaused: 'Session paused',
    sessionResumed: 'Session resumed',
    sessionCompleted: 'Session completed successfully',
    missionAccomplished: 'Mission Accomplished!',
    betterThanExpected: 'Better than Expected!',
    ofTargetCompleted: 'of target completed',
    unassign: 'Unassign',
    unassignConfirm: 'Unassign from Session?',
    unassignMessage: 'Are you sure you want to unassign? The session will go back to Pool To Do.',
    unassignSuccess: 'Unassigned successfully',
    cancelAssignment: 'Cancel',
    cancelAssignmentConfirm: 'Cancel Assignment?',
    cancelAssignmentMessage: 'Are you sure you want to cancel this assignment? You can reassign yourself from Pool To Do.',
    cancelAssignmentSuccess: 'Assignment cancelled',
    boxMarkedAsShipped: 'Box marked as shipped',
    providePauseReason: 'Please provide a pause reason',
    selectShippingLocationError: 'Please select a shipping location',
    noBoxSelected: 'No box selected',

    // Pause Reasons
    breakTime: 'Break Time',
    equipmentIssue: 'Equipment Issue',
    materialShortage: 'Material Shortage',
    qualityIssue: 'Quality Issue',
    shiftChange: 'Shift Change',
    maintenance: 'Maintenance',
    other: 'Other',

    // Complete Reasons
    targetReached: 'Target Output Reached',
    notYetReached: 'Not yet reached',
    endOfShift: 'End of Shift',
    materialDepleted: 'Material Depleted',
    qualityIssues: 'Quality Issues',
    maintenanceRequired: 'Equipment Maintenance Required',
    orderComplete: 'Production Order Complete',

    // Waste Tracking
    wasteTracking: 'Waste Tracking',
    recordWaste: 'Record Waste',
    recording: 'Recording...',
    totalWaste: 'Total Waste',
    wastePercentage: 'Waste %',
    ofMaterial: 'of material',
    materialUsed: 'Material Used',
    wasteType: 'Waste Type',
    wasteTypeRequired: 'Waste Type *',
    weightLbs: 'Weight (lbs)',
    weightLbsRequired: 'Weight (lbs) *',
    disposalMethod: 'Disposal Method',
    disposalMethodRequired: 'Disposal Method *',
    reasonNotes: 'Reason / Notes',
    optionalDescription: 'Optional description...',
    wasteRecords: 'Waste Records',
    wasteSummary: 'Waste Summary',
    noWasteRecorded: 'No waste recorded for this session',
    remainingMaterial: 'Remaining material',
    totalRemaining: 'Total Remaining',
    recorded: 'Recorded',
    entries: 'entries',
    deleteWasteConfirm: 'Are you sure you want to delete this waste record?',
    deleteWasteRecord: 'Delete waste record',
    wasteRecordedSuccess: 'Waste recorded successfully',
    wasteRecordedFailed: 'Failed to record waste',
    wasteDeletedSuccess: 'Waste record deleted',
    wasteDeletedFailed: 'Failed to delete waste record',
    enterValidWeight: 'Please enter a valid weight',
    finishSession: 'Finish Session',
    sessionSummary: 'Session Summary',
    completionReason: 'Completion Reason',
    recordWasteFirst: 'Please record at least one waste entry before finishing',

    // Waste Types
    trim: 'Trim',
    damagedProduct: 'Damaged Product',
    processingLoss: 'Processing Loss',
    wasteTypeByProduct: 'By-product',
    wasteTypeDisposable: 'Disposable',
    wasteTypeHumanError: 'Human Error',
    humanErrorTrackingOnly: 'Tracking only',
    wasteTypeWaterLiquid: 'Water/Liquid',
    wasteTypeRemainder: 'Remainder',
    wasteTypeRemainderIntact: 'Remainder - Intact',
    wasteTypeRemainderNotIntact: 'Remainder - Not Intact',
    remainderIntactDesc: 'Still sealed in original boxes',
    remainderNotIntactDesc: 'Opened/partial materials',
    // Waste tracking sections
    actualWaste: 'Actual Waste',
    countsAsWaste: 'Counts as waste',
    returnsToInventory: 'Returns to Inventory',
    productionRemainder: 'Production Remainder',
    notWaste: 'Not waste',
    createsNewLot: 'Creates new lot',
    addsBackToLot: 'Adds back to original lot',
    selectMaterial: 'Select material...',
    autoCalculated: 'Auto-calculated',
    enterAllWasteWeights: 'Enter weight for all waste types (required, enter 0 if none)',
    fillAllWasteFields: 'Please fill in all waste weight fields',

    // Yield Variance Approval (supervisor verification when variance > 2%)
    yieldVarianceDetected: 'Yield Variance Detected',
    yieldVarianceMessage: 'The actual yield differs from expected by more than 2%. Supervisor approval is required.',
    expectedYield: 'Expected Yield',
    actualYield: 'Actual Yield',
    supervisorApproval: 'Supervisor Approval',
    scanSupervisorBadge: 'Scan supervisor ID badge to approve',
    waitingForBadgeScan: 'Waiting for badge scan...',
    supervisorVerified: 'Supervisor Verified',
    invalidSupervisorBadge: 'Invalid supervisor badge',
    supervisorLogin: 'Supervisor Login',
    supervisorUsername: 'Username',
    supervisorPassword: 'Password',
    varianceReason: 'Reason for Variance',
    varianceReasonPlaceholder: 'Explain why the yield differs from expected...',
    approveAndComplete: 'Approve & Complete',
    invalidSupervisorCredentials: 'Invalid supervisor credentials',
    notAuthorizedSupervisor: 'User is not authorized as supervisor',
    reasonRequired: 'Reason is required',
    verifyingCredentials: 'Verifying...',
    yieldVariance: 'Variance',

    // Remainder-specific (raw material selection) - deprecated
    unusedRawMaterial: 'Unused Raw Material',
    selectRawMaterial: 'Select raw material',
    selectRawMaterialRequired: 'Please select a raw material for the remainder',
    allRemaindersRecorded: 'All raw materials already have remainder recorded',
    noRawMaterialsLinked: 'No raw materials linked to this session',

    // Disposal Methods
    compost: 'Compost',
    animalFeed: 'Animal Feed',
    landfill: 'Landfill',
    incineration: 'Incineration',
    recycling: 'Recycling',

    // Session Pool
    availableSessions: 'Available Sessions Pool',
    selectSessionToWork: 'Select a session to start working',
    availableSessionsCount: 'available sessions',
    refresh: 'Refresh',
    loadingAvailableSessions: 'Loading available sessions...',
    noAvailableSessions: 'No Available Sessions',
    noSessionsInPool: 'There are currently no sessions available in the pool.',
    checkBackLater: 'Check back later or contact your supervisor.',
    sessionNumber: 'Session #',
    available: 'Available',
    assignToMe: 'Assign to Me',
    inQueue: 'In Queue',
    sessionReady: 'Ready',
    waitingInQueue: 'In Queue',
    waitingForAssignment: 'Waiting for assignment',
    assigning: 'Assigning...',
    youHaveActiveSession: 'You Have an Active Session',
    completeCurrentSession: 'Complete your current session before taking a new one from the pool.',
    goToProductionEntry: 'Go to Production Entry',
    confirmAssignment: 'Confirm Session Assignment',
    confirmAssignmentMessage: 'Are you sure you want to assign this session to yourself?',
    oneSessionOnly: 'You can only have one active session at a time.',
    authorizedBy: 'Authorized By',
    created: 'Created',
    scheduledDate: 'Scheduled Date',
    weekOf: 'Week of',
    sessionNotScheduledToday: 'This session is not scheduled for today',
    viewDetails: 'View',
    hideDetails: 'Hide',
    confirm: 'Confirm',

    // Session History
    sessionHistory: 'Session History',
    viewPastSessions: 'View your past production sessions and performance',
    pastSessions: 'Past Sessions',
    page: 'Page',
    noSessionHistory: 'No session history found',
    outputProduced: 'Output Produced',
    wasteGenerated: 'Waste Generated',
    started: 'Started',
    completed: 'Completed',

    // Performance Dashboard
    performanceDashboard: 'Performance Dashboard',
    welcomeBack: 'Welcome back,',
    loadingPerformanceData: 'Loading performance data...',
    period: 'Period',
    week: 'Week',
    month: 'Month',
    sessions: 'Sessions',
    sessionsCompleted: 'Sessions completed',
    output: 'Output',
    totalWeightProduced: 'Total weight produced',
    averageYield: 'Average yield percentage',
    averageYieldPercentage: 'Average yield percentage',
    wasteRatio: 'Waste ratio',
    products: 'Products',
    differentProducts: 'Different products',
    personalBest: 'Personal Best',
    recentSessions: 'Recent Sessions',
    duration: 'Duration',
    noSessionsFound: 'No sessions found',

    // Operator Dashboard
    operatorDashboard: 'Operator Dashboard',
    currentSession: 'Current Session',
    noActiveSession: 'No Active Session',
    noSessionYet: "You don't have an assigned production session yet. Please contact your supervisor.",
    noAssignedSession: "You don't have any assigned production session at the moment.",
    contactSupervisorForSession: 'Please contact your supervisor to get a session assigned.',
    boxesCompleted: 'Boxes Completed',
    timeRunning: 'Time Running',
    recordProduction: 'Record Production',
    sessionsToday: 'Sessions Today',
    notStarted: 'Not started',
    loading: 'Loading...',
    performanceStats: 'Performance Stats',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    sessionsWorked: 'Sessions Worked',
    avgYield: 'Average Yield',
    waste: 'Waste',
    productInfo: 'Product Information',
    sku: 'SKU',
    material: 'Material',
    initialWeight: 'Initial Weight',
    productionProgress: 'Production Progress',
    actual: 'Actual',
    pauseSessionDescription: 'Please provide a reason for pausing the session',
    selectReason: 'Select a reason...',
    sessionStartedSuccess: 'Session started successfully',
    sessionStartedFailed: 'Failed to start session',
    sessionPausedSuccess: 'Session paused',
    sessionPausedFailed: 'Failed to pause session',
    sessionResumedSuccess: 'Session resumed',
    sessionResumedFailed: 'Failed to resume session',

    // Status
    packed: 'Packed',
    cancelled: 'Cancelled',
    pending: 'Pending',
    submitted: 'Submitted',
    received: 'Received',
    planned: 'Received',

    // Common
    lbs: 'lbs',
    boxes: 'Boxes',
    session: 'Session',
    noStationAssigned: 'No station assigned',

    // Scale Connection
    scaleConnecting: 'Connecting to scale...',
    scaleConnected: 'Scale connected',
    scaleDisconnected: 'Scale disconnected',
    scaleConnectionError: 'Scale connection error',
    scaleReconnecting: 'Reconnecting to scale...',
    scaleConnectionLost: 'Connection lost. Max reconnection attempts reached.',
    scaleWeightStable: 'Weight is stable',
    scaleWeightUnstable: 'Weight is fluctuating',
    scaleLastUpdate: 'Last update',
    scaleTareCommand: 'Tare',
    scaleNotConnected: 'Scale not connected',
    autoMode: 'Auto Mode',
    manualMode: 'Manual Mode',
    scaleMode: 'Scale',
    captureWeight: 'Capture',
    scaleConnectedWaste: 'Scale Connected',
    scaleDisconnectedWaste: 'Scale Disconnected',
    stable: 'Stable',
    unaccountedMaterial: 'Unaccounted Material',
    allMaterialAccounted: 'All material has been accounted for!',
    trackRemainingWaste: 'Track the remaining waste to finish session',
  },

  // Supervisor
  supervisor: {
    supervisor: 'Supervisor',
    dashboard: 'Supervisor Dashboard',
    realtimeMonitoring: 'Real-time production monitoring and control',
    activeStations: 'Active Stations',
    totalStations: 'total stations',
    activeSessions: 'Active Sessions',
    plannedSessions: 'Submitted Sessions',
    completedSessions: 'Completed Sessions',
    cancelledSessions: 'Cancelled Sessions',
    productionInProgress: 'Production in progress',
    awaitingStart: 'Awaiting start',
    todaysProduction: "Today's Production",
    boxes: 'Boxes',
    totalWeight: 'Total Weight',
    yieldRate: 'Yield Rate',
    productionVsTarget: 'Production vs Target',
    pendingAlerts: 'Pending Alerts',
    requiresAttention: 'Requires attention',
    dismissAlerts: 'Dismiss alerts',
    clickToShowAlerts: 'Click to show alerts',
    supervisorDashboard: 'Supervisor Dashboard',
    allLocations: 'All Locations',
    refresh: 'Refresh',
    quickActions: 'Quick Actions',
    quickStats: 'Quick Stats',
    setWeeklyOperatorSchedule: 'Set Weekly Operator Schedule',
    openStations: 'Open Stations',
    openStationsDescription: 'Run auto-promotion on all stations',
    promotionResults: 'Promotion Results',
    promotedSessions: 'The following sessions were promoted to ready:',
    noPromotions: 'No sessions are ready to be promoted. All stations are either busy, have no queued sessions, or sessions are missing operators/materials.',
    alert: 'Alert',
    plural: 's',
    requireAttention: 'Require Attention',
    is: 'is',
    hasNoAssignedProduct: 'has no assigned product',
    loadingStationData: 'Loading station data...',
    noStationsFound: 'No stations found',
    station: 'Station',
    active: 'active',
    progress: 'Progress',
    type: 'Type',
    production: 'Production',
    targetWeight: 'Target Weight',
    assignedProduct: 'Assigned Product',
    rawMaterial: 'Raw Material',
    initialWeight: 'Initial Weight',
    targetOutput: 'Target Output',
    actualOutput: 'Actual Output',
    startedAt: 'Started',
    yield: 'Yield',
    parentSession: 'Parent Session',
    finishedProduct: 'Finished Product',
    operator: 'Operator',
    packer: 'Packer',
    notAssigned: 'Not assigned',
    reassignOperator: 'Reassign Operator',
    isCurrentlyAssignedTo: 'is currently assigned to',
    doYouWantToReassignTo: 'Do you want to reassign them to',
    reassign: 'Reassign',
    viewDetails: 'View Details',
    pause: 'Pause',
    recentActivity: 'Recent Activity',
    loadingActivities: 'Loading activities...',
    noRecentActivity: 'No recent activity',
    systemEventsWillAppear: 'System events will appear here in real-time',
    recentActivityUnderDevelopment: 'Recent Activity feature is under development',
    checkBackSoon: 'Check back soon for real-time activity updates',

    // Mobile Session Management
    sessions: 'sessions',
    activeAndPlanned: 'Active & Submitted',
    paused: 'Paused',
    completed: 'Completed',
    noSessionsFound: 'No sessions found',

    // Camera Monitoring
    cameraMonitoringDashboard: 'Camera Monitoring Dashboard',
    monitorAllCCTV: 'Monitor all CCTV feeds across all stations',
    totalCameras: 'total cameras',
    refreshAll: 'Refresh All',
    loadingCameras: 'Loading cameras...',
    configureFirstCamera: 'No cameras have been set up yet. Configure cameras for your stations to start monitoring.',
    stopViewing: 'Stop viewing',
    startViewing: 'Start viewing',
    fullscreen: 'Fullscreen',
    stream: 'stream',
    requiresMediaServer: 'Requires media server',
    clickEyeToView: 'Click eye to view',
    closeESC: 'Close (ESC)',

    // Override History
    overrideHistory: 'Override History',
    overrideHistoryDesc: 'View all supervisor overrides and approvals',
    overrideTypes: 'Override Types',
    filters: 'Filters',
    dateFrom: 'Date From',
    dateTo: 'Date To',
    overrideType: 'Override Type',
    searchSupervisor: 'Search supervisor...',
    exportCSV: 'Export CSV',
    timestamp: 'Timestamp',
    session: 'Session',
    details: 'Details',
    reason: 'Reason',
    noOverridesFound: 'No overrides found',
    additionalDetails: 'Additional Details',
    showingResults: 'Showing {{from}}-{{to}} of {{total}}',
    page: 'Page',
    labelReprintDesc: 'Reprinting a box label',
    manualWeightDesc: 'Manual weight entry',
    yieldSignoffDesc: 'Approving yield variance',
    eodSignoffDesc: 'End of day sign-off',
    sessionCompletionDesc: 'Early session completion',
    override_session_completion: 'Session Completion Override',
    override_eod_signoff: 'EOD Sign-off Override',

    // Station Management Page
    loadingStations: 'Loading stations...',
    monitorStationsAssignSessions: 'Monitor stations, assign sessions, and manage operators',
    location: 'Location',
    product: 'Product',
    itemNumber: 'Item Number',
    target: 'Target',
    started: 'Started',
    noSessionAssigned: 'No session assigned',
    assignedOperator: 'Assigned Operator',
    noOperatorAssigned: 'No operator assigned',
    assignSession: 'Assign Session',
    changeOperator: 'Change Operator',
    assignOperator: 'Assign Operator',
    startSession: 'Start Session',
    pauseSession: 'Pause Session',
    resumeSession: 'Resume Session',
    completeSession: 'Complete Session',
    starting: 'Starting...',
    pausing: 'Pausing...',
    resuming: 'Resuming...',
    completing: 'Completing...',
    assigning: 'Assigning...',
    assignSessionTo: 'Assign Session to',
    warning: 'Warning',
    stationAlreadyHasSession: 'This station already has a session assigned. Assigning a new session will replace the current one.',
    selectPlannedSession: 'Select Submitted Session',
    onlyPlannedSessionsShown: 'Only submitted sessions without a station are shown. Each station can only have one active session at a time.',
    selectASession: '-- Select a session --',
    assignOperatorTo: 'Assign Operator to',
    selectOperator: 'Select Operator',
    selectAnOperator: '-- Select an operator --',
    sessionAssignedSuccess: 'Session assigned to station successfully',
    failedToAssignSession: 'Failed to assign session',
    operatorAssignedSuccess: 'Operator assigned successfully',
    failedToAssignOperator: 'Failed to assign operator',
    sessionStartedSuccess: 'Session started successfully',
    failedToStartSession: 'Failed to start session',
    sessionPausedSuccess: 'Session paused successfully',
    failedToPauseSession: 'Failed to pause session',
    sessionResumedSuccess: 'Session resumed successfully',
    failedToResumeSession: 'Failed to resume session',
    sessionCompletedSuccess: 'Session completed successfully',
    failedToCompleteSession: 'Failed to complete session',
    sessionUnplannedSuccess: 'Session removed successfully',
    failedToUnplanSession: 'Failed to remove session',
    removeSession: 'Remove Session',
    removingSession: 'Removing...',
    showDisabledStations: 'Show Disabled',
    hideDisabledStations: 'Hide Disabled',

    // Operator Assignment
    assignOperatorNow: 'Assign Operator Now?',
    sessionAssignedToStation: 'Session has been assigned to {station}.',
    operatorRequiredToStart: 'An operator must be assigned before the session can be started.',
    assignLater: 'Assign Later',
    noOperator: 'No Operator',
    assignOperatorFirst: 'Please assign an operator before starting the session',

    // Queue and Ready Status
    inQueue: 'in queue',
    sessionReady: 'Ready',
    queueCount: '{{count}} in queue',
    waitingForAssignment: 'Waiting for assignment',
    waitingInQueue: 'In queue',

    // Material Restriction
    allowedMaterialType: 'Allowed Material Type',
    selectMaterialType: '-- No Restriction --',
    materialTypeChicken: 'Raw Chicken',
    materialTypeBeef: 'Beef',
    materialTypePork: 'Pork',
    materialTypeGoatLamb: 'Goat/Lamb',
    restrictedTo: 'Restricted to',
    materialTypeUpdated: 'Material type restriction updated successfully',
    cleaningConfirmationRequired: 'Cleaning Confirmation Required',
    materialChangeWarning: 'Changing the raw material type requires station cleaning to prevent cross-contamination.',
    previousType: 'Previous Type',
    newType: 'New Type',
    confirmCleaning: 'Has the station been properly cleaned?',
    cleaningStep1: 'All equipment and surfaces sanitized',
    cleaningStep2: 'Previous material residue removed',
    cleaningStep3: 'Station inspection completed',
    cleaningStep4: 'Ready for new material type',
    confirmCleaningComplete: 'Yes, Cleaning Complete',
    materialTypeMismatch: 'Material type mismatch',
    sessionUses: 'This session uses',
    cannotDetermineMaterialType: 'Cannot determine material type',

    // Production Planning
    productionPlanning: 'Production Planning',
    createManageSessions: 'Create and manage production sessions',
    newProductionSession: 'New Production Session',
    createProductionSession: 'Create Production Session',
    validationErrors: 'Validation Errors',

    // Multi-Station Mode
    stationAssignment: 'Station Assignment',
    singleStation: 'Single',
    multiStation: 'Multi-Station',
    multiStationMode: 'Multi-Station Mode',
    multiStationModeDescription: 'Split materials across multiple stations. Each station will work as an independent sub-session.',
    materialAllocationStatus: 'Material Allocation Status',
    fullyAllocated: 'Fully allocated',
    unallocated: 'unallocated',
    addStation: 'Add Station',
    materialAllocation: 'Material Allocation',
    stationSubSessions: 'Station Sub-Sessions',
    stationsCount: 'stations',
    addRawMaterialsFirst: 'Add raw materials above before configuring station allocations',
    eachStationMustHaveMaterial: 'Each station must have at least some material allocated',
    allMaterialMustBeAllocated: 'All material weight must be allocated to stations. Check allocation totals.',
    selectStationForAllAllocations: 'Please select a station for all allocations',
    eachStationCanOnlyBeAssignedOnce: 'Each station can only be assigned once',
    atLeastOneStationRequired: 'At least one station must be allocated in multi-station mode',

    // Station Details Modal (uses keys from stations section)
    stationId: 'Station ID',
    locationInformation: 'Location Information',
    locationName: 'Location Name',
    locationCode: 'Location Code',
    warehouseCode: 'Warehouse Code',
    configuration: 'Configuration',
    stationType: 'Station Type',
    notSet: 'Not set',
    sessionStatus: 'Session Status',
    close: 'Close',

    // Camera Management (from earlier section)
    cameraManagement: 'Camera Management',
    configureCCTVCameras: 'Configure CCTV cameras for station monitoring',
    addCamera: 'Add Camera',
    camera: 'camera',
    noCamerasConfigured: 'No Cameras Configured',
    addFirstCamera: 'Add your first camera to start monitoring stations',
    status: 'Status',
    position: 'Position',
    resolution: 'Resolution',
    cameraAddedSuccessfully: 'Camera added successfully',
    failedToAddCamera: 'Failed to add camera',
    cameraUpdatedSuccessfully: 'Camera updated successfully',
    failedToUpdateCamera: 'Failed to update camera',
    cameraDeletedSuccessfully: 'Camera deleted successfully',
    failedToDeleteCamera: 'Failed to delete camera',
    pleaseFillRequiredFields: 'Please fill in all required fields',
    confirmDeleteCamera: 'Are you sure you want to delete this camera?',
    editCamera: 'Edit Camera',
    addNewCamera: 'Add New Camera',
    selectStation: 'Select a station',
    cameraName: 'Camera Name',
    cameraNamePlaceholder: 'e.g., Station 1 - Overhead',
    cameraURL: 'Camera URL',
    supportsStreamURLs: 'Supports RTSP, HTTP, MJPEG, or HLS stream URLs',
    cameraType: 'Camera Type',
    positionPlaceholder: 'e.g., overhead, side-left',
    notes: 'Notes',
    notesPlaceholder: 'Additional notes about this camera...',
    updateCamera: 'Update Camera',

    // Label Management
    labelManagement: 'Label Management',
    manageAndReprintLabels: 'Manage and reprint labels for packed boxes',
    productionDate: 'Production Date',
    selectSession: 'Select Session',
    selectSessionPlaceholder: '-- Select a session --',
    noSessionsForDate: 'No sessions found for this date',
    filterByStatus: 'Filter by Status',
    allBoxes: 'All Boxes',
    readyBoxes: 'Packed Boxes',
    shippedBoxes: 'Shipped Boxes',
    totalBoxes: 'Total Boxes',
    boxesWithReprints: 'Boxes with Reprints',
    boxNumber: 'Box Number',
    weight: 'Weight',
    packedBy: 'Packed By',
    labelStatus: 'Label Status',
    lastPrinted: 'Last Printed',
    noActiveLabel: 'No Active Label',
    reprinted: 'Reprinted',
    original: 'Original',
    viewHistory: 'View History',
    reprintLabel: 'Reprint Label',
    noBoxesFound: 'No boxes found for the selected session and status.',
    labelPrintHistory: 'Label Print History',
    printNumber: 'Print #',
    printedAt: 'Printed At',
    printedBy: 'Printed By',
    discardedAt: 'Discarded At',
    discardedBy: 'Discarded By',
    discardReason: 'Discard Reason',
    noLabelHistory: 'No label print history found for this box.',
    reprintWarning: 'This will mark the current label as discarded and print a new one.',
    reprintReason: 'Reprint Reason',
    selectReprintReason: 'Select a reason...',
    reprintReasonPlaceholder: 'Enter the reason for reprinting the label...',
    confirmReprint: 'Confirm Reprint',
    pleaseProvideReason: 'Please provide a reason for reprinting',
    labelReprintedSuccess: 'Label reprinted successfully',
    failedToReprintLabel: 'Failed to reprint label',

    // Printer Configuration
    labelPrinterConnected: 'Label Printer Connected',
    noLabelPrinter: 'No Label Printer',
    directPrintingEnabled: 'Direct printing enabled',
    willUseBrowserDialog: 'Will use browser print dialog',
    connectPrinter: 'Connect Printer',
    detecting: 'Detecting...',
    printDirect: 'Print Direct',

    // Station Management (uses keys from stations section)
    stationManagement: 'Station Management',
    manageStations: 'Manage production stations across all locations',
    bulkAssignProducts: 'Bulk Assign Products',
    totalStationsCount: 'Total Stations',
    warehouse: 'Warehouse',
    allStatuses: 'All Statuses',
    idle: 'Idle',
    idleStations: 'Idle Stations',
    unassignedSessions: 'Unassigned Sessions',
    noStationAssigned: 'No Station Assigned',
    materialOnHold: 'Material On Hold',
    cannotDeactivate: 'Cannot deactivate station with active session. Product:',
    pleaseCompleteSession: 'Please complete or cancel the session first.',
    deactivate: 'Deactivate',
    activate: 'Activate',

    // Station Enable/Disable
    stationEnabled: 'Station enabled',
    stationDisabled: 'Station disabled',
    failedToToggleStation: 'Failed to toggle station status',
    cannotDisableWithActiveSession: 'Cannot disable station with an active session',
    clickToEnable: 'Click to enable station',
    clickToDisable: 'Click to disable station',
    disabled: 'Disabled',
    stationsEnabled: 'stations enabled',
    assign: 'Assign',

    // Schedule View
    addNewSchedule: 'Add New Production Session',
    byWeight: 'By Weight',
    byBoxes: 'By Cases',
    sessionCode: 'Session Code',
    scheduleDate: 'Date',
    transferDate: 'Transfer Date',
    dateRangeHint: 'Date can be set from today up to 3 weeks ahead',
    manpowerForDate: 'Scheduled Manpower',
    operators: 'Operators',
    packers: 'Packers',
    stations: 'Stations',
    noManpowerScheduled: 'No manpower scheduled for this date',
    noManpowerWarning: 'Awaiting supervisor to schedule operators',
    sourceWarehouse: 'Source Location',
    sourceWarehouseHint: 'If selected, an inbound transfer will be auto-created',
    sourceWarehouseTooltip: 'An inbound transfer will be auto-created',
    sourceWarehouseRequiredHint: 'An inbound transfer will be auto-created',
    noInboundTransferNeeded: 'No inbound transfer needed for this production session. Sufficient raw material in the facility to cover it.',
    transferBackDestination: 'Transfer Back Destination',
    transferBackDestinationHint: 'An outbound transfer will be auto-created the scheduled day of production',
    transferBackDestinations: 'Transfer Back Destinations',
    addDestination: 'Add destination',
    selectDestination: 'Select…',
    duplicateDestination: 'Each destination only once per session.',
    transferBackDestinationsHint: 'Each becomes an outbound transfer',
    transferBackRebalanceAfterReduce: 'Materials were reduced. Please rebalance transfer-back destinations.',
    casesUnit: 'cases',
    scheduleItemNumber: 'Finished Product',
    scheduleProductName: 'Product Name',
    packing: 'Packing',
    cases: 'Finish Product cs',
    targetBoxes: 'Target Boxes',
    targetCases: 'Target Cases',
    estimatedLbs: 'Est. lbs',
    material: 'Raw Material Item #',
    primaryMaterial: 'Primary Product',
    selectedMaterials: 'Selected Materials',
    laborFactor: 'Labor',
    selectMaterialForLaborFactor: 'Select a raw material to view Labour Factor',
    labourEstimatedOutput: 'Estimated Output',
    labourEstimatedOutputCases: '≈ {{count}} cases',
    labourMaxForDay: 'Max for the day',
    labourWithAllowance: 'With allowance (−{{pct}}%)',
    labourMaxApplyHint: 'Click to set Target Cases',
    labourRemainder: 'Remainder',
    labourWorkers: 'Workers',
    labourSessions: 'Sessions',
    labourDuration: 'Duration (h)',
    labourBasedOnInput: 'Based on {{weight}} lbs input',
    labourNoFactor: 'No Labour Factor',
    labourYieldOnlyEstimate: 'yield only',
    meanProduction: 'Mean Prod #',
    scheduleBrand: 'Brand',
    multipleBrands: 'Multiple',
    scheduleLocation: 'Location',
    noScheduledSessions: 'No sessions scheduled for this date',

    // Multi-material form
    lbsInput: 'Weight (lbs)',
    unitWeight: 'Unit Weight',
    estOutput: 'Est. Output lbs',
    estInput: 'Est. Input lbs',
    estUsableMaterial: 'Est. Usable (lbs)',
    estimatedBoxes: 'Est. Boxes',
    byproducts: 'Secondary Product / By-Product',
    estCases: 'Raw Material cs',
    caseWeight: 'Case Weight',
    requiredWeight: 'Req. Weight (lbs)',
    addMaterial: 'Add Material',
    exceedsStock: 'Exceeds available stock',
    partialTransferNeeded: 'Transfer needed',
    yieldNotConfigured: 'No Yield',
    totalLbs: 'Total Weight',
    totalCases: 'Total Cases',
    totalInputWeight: 'Total Input Weight',
    totalOutputWeight: 'Total Output Weight',
    totalEstUsable: 'Est. Usable',
    availableStock: 'Available HTE',
    icsBalance: 'Inventory',
    frozen: 'Frozen',
    fresh: 'Fresh',
    availableAtHte: 'at HTE',
    insufficientSourceStock: 'Insufficient stock at',
    onlyHas: 'only has',
    needed: 'needed',
    belowSafeStockAt: 'Below safe quantity at',
    csUsable: 'usable',
    csSafetyStock: 'safety stock',
    outOfStockTitle: 'Out of Stock',
    belowSafeQtyTitle: 'Below Safe Quantity',
    outOfStockBodyPrefix: 'The following items are out of stock at',
    outOfStockBodySuffix: "per the inventory service. Stock data may be inaccurate — you can override and proceed if you've verified availability in person.",
    safeQtyIntroPrefix: 'Taking the full amount would leave',
    safeQtyIntroSuffix: 'below its safe quantity. The request will be reduced:',
    csOnHand: 'on hand',
    casesAvailable: 'CS available',
    override: 'Override',
    overrideAndProceed: 'Override and Proceed',
    onHold: 'on hold',
    materialsOnHold: 'Approved materials (on hold)',
    materialsInHTE: 'Available materials (in HTE)',
    totalAvailable: 'Total available',
    totalAvailableAtHte: 'Total Available at HTE:',
    includingOnHold: 'Including on-hold',
    onHoldApprovedBadge: 'Approved',
    selectProductError: 'Please select a product',
    pastDateError: 'Scheduled date cannot be in the past',
    weekendWarningProduction: 'Selected Production Date is a {{day}}',
    weekendWarningTransfer: 'Selected Transfer Date is a {{day}}',
    selectMaterialError: 'Please add at least one raw material with weight',
    exceedsStockError: 'One or more materials exceed available stock',

    // Inline raw material linking
    otherProducts: 'Other Products (no materials linked)',
    noMaterials: 'NEW',
    noMaterialsLinked: 'No raw materials linked to this product',
    addMaterialsPrompt: 'Search and add raw materials to continue creating a session.',
    searchRawMaterials: 'Search raw materials by name or SKU...',
    materialLinked: 'Linked',
    materialLinkError: 'Failed to link material',
    materialLinkSuccess: 'Raw material linked successfully',
    materialUnlinked: 'Raw material removed',
    setPreference: 'Set preference for',

    // Material sort options
    sortPreference: 'Preference',
    sortYieldHigh: 'Yield ↓',
    sortYieldLow: 'Yield ↑',
    sortLaborHigh: 'Labor ↓',
    sortLaborLow: 'Labor ↑',
    sortAvailabilityHigh: 'Stock ↓',
    sortAvailabilityLow: 'Stock ↑',

    // Export
    exportToExcel: 'Export to Excel',
    exportSessions: 'Export Sessions',
    selectDateRange: 'Select Date Range',
    startDate: 'Start Date',
    endDate: 'End Date',
    sessionsToExport: 'Sessions to export',
    noSessionsInRange: 'No sessions found in this date range',
    exporting: 'Exporting...',

    // Edit/Delete session
    editSession: 'Edit Session',
    editProductionSession: 'Edit Production Session',
    sessionUpdatedSuccess: 'Session updated successfully',
    failedToUpdateSession: 'Failed to update session',
    confirmCancelSession: 'Cancel Session?',
    cancelSessionWarning: 'This action cannot be undone. The session will be marked as cancelled.',
    forceCancelActiveSession: 'Force Cancel Active Session?',
    forceCancelWarning: 'This session is currently active. Cancelling will release the station and unassign the operator. Any unsaved progress will be lost.',
    sessionCancelledSuccess: 'Session cancelled successfully',
    failedToCancelSession: 'Failed to cancel session',

    // Incomplete Sessions Alert
    incompleteSessionsAlert: 'Incomplete Sessions from Previous Days',
    incompleteSessionsCount: 'incomplete session(s) from previous days require attention',
    incompleteSessionsDescription: 'The following sessions were not completed and may need to be resolved:',
    scheduledFor: 'Scheduled for',
    viewInPlanning: 'View in Production Planning',
    dismissIncompleteAlert: 'Dismiss',

    // Pending Approvals Alert
    pendingApprovalsAlert: 'Pending Approvals',

    // Dashboard KPI sub-card labels
    todaysSessions: "Today's Sessions",
    planned: 'Submitted',
    activeLabel: 'Active',
    completedLabel: 'Completed',
    avgYield: 'Avg Yield',
    enabledLabel: 'Enabled',
    stationOverview: 'Station Overview',

    // Production Progress Overview
    productionProgressOverview: 'Production Progress Overview',
    overall: 'Overall',
    noSessionsToday: 'No production sessions scheduled for today',
    lbs: 'lbs',
  },

  // Mobile Supervisor View
  mobileSupervisor: {
    title: 'Supervisor',
    subtitle: 'Mobile Dashboard',
    stationOverview: 'Station Overview',
    stationOverviewDesc: 'View all station status',
    sessionManagement: 'Sessions',
    sessionManagementDesc: 'Manage production sessions',
    productionPlanning: 'New Session',
    productionPlanningDesc: 'Create production session',
    transferVerification: 'Receiving',
    transferVerificationDesc: 'Verify box transfers',
    receiveVerify: 'Transfers',
    receiveVerifyDesc: 'Receive incoming transfers',
    transfers: 'Transfers',
    transfersDesc: 'Receive & view transfers',
    receive: 'Receive',
    receiveDesc: 'Receive incoming transfers',
    transferOption: 'Send',
    transferOptionDesc: 'Transfer back to warehouse',
    allTransfers: 'All Transfers',
    allTransfersDesc: 'View all transfer orders',
    inventoryDesc: 'View materials & stock',
    // Scan Options Modal
    scanOptions: 'What do you want to scan?',
    labelLookup: 'Label Lookup',
    labelLookupDesc: 'Scan a box label to view details',
    scanStation: 'Scan Station',
    scanStationDesc: 'Scan station QR to view status',
    endOfDaySummary: 'Daily Summary',
    endOfDaySummaryDesc: 'Production stats',
    overrideHistory: 'Override History',
    overrideHistoryDesc: 'View past overrides',
    // Override types
    manualWeight: 'Manual Weight',
    labelReprint: 'Label Reprint',
    sessionCompletion: 'Session Completion',
    stationManagement: 'Station Settings',
    stationManagementDesc: 'Enable/disable stations',
    // Scan Verification
    scanBoxBarcode: 'Scan Box Barcode',
    scanBoxHint: 'Point scanner at GS1 barcode on box',
    boxFound: 'Box found',
    boxNotFound: 'Box not found',
    reprintLabel: 'Reprint Label',
    reprintingLabel: 'Reprinting label...',
    labelReprinted: 'Label sent to printer',
    scanAnother: 'Scan Another',
    // Transfer Verification
    scanToVerify: 'Scan Box to Verify',
    scanTransferBoxHint: 'Scan box barcode to verify transfer',
    pendingTransfers: 'pending',
    verifiedThisSession: 'verified',
    transferVerified: 'Transfer verified',
    transferRejected: 'Transfer rejected',
    boxesVerified: 'boxes verified this session',
    verify: 'Verify',
    reject: 'Reject',
    from: 'From',
    to: 'To',
    // Station Settings
    stationEnabled: 'Station enabled',
    stationDisabled: 'Station disabled',
    cannotDisableActiveStation: 'Cannot disable while session is active',
    // Production Planning
    selectProduct: 'Select Product',
    selectStation: 'Select Station',
    selectMaterial: 'Select Material',
    confirmSession: 'Confirm Session',
    noAvailableStations: 'No available stations',
    createSession: 'Create Session',
    sessionCreated: 'Session created',
  },

  // Production Monitor
  productionMonitor: {
    title: 'Production Monitor',
    scheduleView: 'Schedule View',
    sessionManagement: 'Session Management',
    activeSessions: 'Active Sessions',
    operatorsWorking: 'Operators Working',
    todaysOutput: "Today's Output",
    sessionsAtRisk: 'Sessions At Risk',
    activeSessionsTable: 'Active Sessions',
    autoRefreshNote: 'Auto-refreshes every 30 seconds',
    noActiveSessions: 'No active sessions at the moment',
    session: 'Session',
    product: 'Product',
    operator: 'Operator',
    station: 'Station',
    elapsed: 'Elapsed',
    progress: 'Progress',
    output: 'Output',
    pace: 'Pace',
    status: 'Status',
    estCompletion: 'Est. Completion',
    projOutput: 'Proj. Output',
    projRemaining: 'Proj. Remaining',
    actualOutput: 'Actual Output',
    remaining: 'Remaining',
    onTrack: 'On Track',
    atRisk: 'At Risk',
    behind: 'Behind',
    subSessions: 'sub-sessions',
    multiple: 'Multiple',
    columns: 'Columns',
    completedSessions: 'Completed Sessions',
    cancelledSessions: 'Cancelled Sessions',
    reason: 'Reason',
  },

  // Admin Pages
  admin: {
    // Users Page
    users: 'Users',
    manageUserAccounts: 'Manage user accounts and permissions',
    addUser: 'Add User',
    searchUsers: 'Search users...',
    allRoles: 'All Roles',
    admin: 'Admin',
    supervisor: 'Supervisor',
    manager: 'Manager',
    operator: 'Operator',
    warehouse: 'Warehouse',
    route: 'Route',
    user: 'User',
    role: 'Role',
    lastLogin: 'Last Login',
    active: 'Active',
    inactive: 'Inactive',
    editUser: 'Edit User',
    deactivateUser: 'Deactivate User',
    activateUser: 'Activate User',
    deleteUser: 'Delete User',
    noUsersFound: 'No users found',
    tryAdjustingSearch: 'Try adjusting your search criteria.',
    getStartedCreatingUser: 'Get started by creating a new user.',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    admins: 'Admins',
    confirmDeleteUser: 'Are you sure you want to delete',
    actionCannotBeUndone: 'This action cannot be undone.',
    userDeletedSuccess: 'User deleted successfully',
    failedToDeleteUser: 'Failed to delete user',
    userStatusUpdated: 'User status updated successfully',
    failedToUpdateUserStatus: 'Failed to update user status',
    failedToLoadUsers: 'Failed to load users',
    pleaseRefresh: 'Please refresh and try again.',

    // Settings Page
    systemSettings: 'System Settings',
    configureSystemPreferences: 'Configure system preferences and options',
    reset: 'Reset',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    general: 'General',
    notifications: 'Notifications',
    security: 'Security',
    production: 'Production',
    system: 'System',
    generalSettings: 'General Settings',
    companyName: 'Company Name',
    timezone: 'Timezone',
    dateFormat: 'Date Format',
    currency: 'Currency',
    language: 'Language',
    notificationPreferences: 'Notification Preferences',
    emailNotifications: 'Email Notifications',
    emailNotificationsDesc: 'Receive important updates via email',
    lowStockAlerts: 'Low Stock Alerts',
    lowStockAlertsDesc: 'Get notified when inventory is running low',
    stationOfflineAlerts: 'Station Offline Alerts',
    stationOfflineAlertsDesc: 'Alert when production stations go offline',
    productionAlerts: 'Production Alerts',
    productionAlertsDesc: 'Notifications for production milestones',
    dailyReports: 'Daily Reports',
    dailyReportsDesc: 'Receive automated daily production reports',
    securitySettings: 'Security Settings',
    sessionTimeout: 'Session Timeout (minutes)',
    sessionTimeoutDesc: 'Users will be logged out after this period of inactivity',
    passwordMinLength: 'Password Minimum Length',
    maxLoginAttempts: 'Max Login Attempts',
    maxLoginAttemptsDesc: 'Account will be locked after this many failed attempts',
    requireStrongPassword: 'Require Strong Passwords',
    requireStrongPasswordDesc: 'Enforce uppercase, lowercase, numbers, and special characters',
    enableTwoFactor: 'Enable Two-Factor Authentication',
    enableTwoFactorDesc: 'Require additional verification for all users',
    labelPrinting: 'Label Printing',
    productionSettings: 'Production Settings',
    defaultTargetWeight: 'Default Target Weight (kg)',
    autoAssignOperators: 'Auto-Assign Operators',
    autoAssignOperatorsDesc: 'Automatically assign available operators to stations',
    allowManualWeightEntry: 'Allow Manual Weight Entry',
    allowManualWeightEntryDesc: 'Permit operators to manually enter weights',
    simulatePrinter: 'Simulate Printer',
    simulatePrinterDesc: 'When ON, printing is bypassed — no labels are sent to the physical printer',
    simulatePrinterPrintSkipped: 'Simulate mode: Print skipped — label preview shown',
    labelCopies: 'Label Copies',
    labelCopiesDesc: 'Number of label copies to print per box',
    printerSubnet: 'Printer Network Subnet',
    printerSubnetDesc: 'First 3 octets of your network (e.g. 192.168.1)',
    printServiceUrl: 'Print Service URL',
    printServiceUrlDesc: 'HTTPS URL of the factory print service (e.g. https://192.168.1.100:3010). Leave empty for local development.',
    registeredPrinters: 'Registered Printers',
    registeredPrintersDesc: 'Register printers and assign them to stations. Changes are reflected in Station Management.',
    addPrinter: 'Add Printer',
    printerName: 'Printer Name',
    printerNamePlaceholder: 'e.g. printer_1',
    printerMacValue: 'MAC Address',
    noPrintersRegistered: 'No printers registered yet',
    printerAdded: 'Printer registered successfully',
    printerDeleted: 'Printer removed',
    printerStaticIp: 'Static IP (optional)',
    printerStaticIpPlaceholder: 'e.g. 172.168.36.179',
    printerIpSaved: 'Printer IP saved',
    printerIpCleared: 'Printer IP removed',
    invalidIpAddress: 'Invalid IP address format',
    confirmDeletePrinter: 'Remove this printer?',
    assignStationToPrinter: 'Assign station...',
    noStationsAvailable: 'No stations available',
    assignedElsewhere: 'assigned to another printer',
    supervisorOffice: "Supervisor's Office",
    noStationAssigned: 'No Station Assigned',
    printerNickname: 'Nickname',
    printerIpAddress: 'IP Address',
    printerPort: 'Port',
    printerStation: 'Station',
    selectStation: 'Select a station...',
    selectPrinter: 'Select a printer...',
    showMac: 'Show MAC',
    hideMac: 'Hide MAC',
    noIpSet: 'No IP set',
    printerTestPanel: 'Test Print',
    printerTestPanelDesc: 'Send a test label to verify printer connectivity.',
    printTestLabel: 'Print Test Label',
    // Batch Label Print
    batchLabelPrint: 'Batch Label Print',
    batchLabelPrintDesc: 'Pre-print labels in bulk for a finished product without running a production session.',
    batchProduct: 'Product',
    batchSelectProduct: 'Select a product...',
    batchNumberOfBoxes: 'Number of Boxes',
    batchDateTime: 'Date & Time',
    batchPrinter: 'Printer',
    batchTotalLabels: 'Total labels',
    batchBoxes: 'boxes',
    batchCopies: 'copies',
    batchPrintButton: 'Print Labels',
    batchPrinting: 'Printing...',
    batchPrintingBox: 'Printing box',
    batchPrintSuccess: 'Batch print completed successfully',
    batchPrintSimulated: 'Batch print simulated successfully',
    batchPrintFailed: 'Batch print failed',
    batchPrintLabels: 'labels',
    printerNotConfigured: 'No printer assigned to this station. Assign a printer in Settings → Label Printing.',
    printerNotResponding: 'Printer not responding.\nTurn the printer off, wait 1 minute, then turn it back on and try again. If it still fails, call your supervisor.',
    printResultUnknown: 'The printer stopped responding while printing.\nCheck whether the label printed before you try again. If you are not sure, call your supervisor.',
    stationServiceDown: 'Cannot reach station service to look up printer. Please try again.',
    arpLookupFailed: 'Printer could not be found on the network. Ensure it is powered on and connected.',

    advancedSettings: 'Advanced',

    // Scale Management (Settings → Scales tab)
    scales: 'Scales',
    registeredScales: 'Registered Scales',
    registeredScalesDesc: 'Register network scales and assign them to stations. One scale per station.',
    addScale: 'Add Scale',
    scaleNickname: 'Nickname',
    scaleNicknamePlaceholder: 'e.g. Main Scale',
    scaleIpAddress: 'IP Address',
    scaleIpPlaceholder: '172.168.110.227',
    scalePort: 'Port',
    scalePortPlaceholder: '1701',
    scaleStation: 'Station',
    noScalesRegistered: 'No scales registered yet',
    scaleAdded: 'Scale registered successfully',
    scaleDeleted: 'Scale removed',
    confirmDeleteScale: 'Remove this scale?',
    scaleTestConnection: 'Test Connection',
    scaleTestSuccess: 'Connection successful',
    scaleTestFailed: 'Connection failed',
    scaleTestPanel: 'Scale Test',
    scaleTestPanelDesc: 'Test scale connectivity and view live weight readings.',
    selectScale: 'Select a scale...',
    scaleDeviceInfo: 'Device Info',
    scaleLiveWeight: 'Live Weight',
    scaleRawLog: 'Raw Log',
    scaleNoMessages: 'No messages yet',
    scaleTesting: 'Testing...',
    scaleReadMode: 'Read Mode',
    scaleModeContinuous: 'Continuous',
    scaleModeOnPrint: 'On Print',
    scaleModeContinuousDesc: 'Polls weight every 500ms',
    scaleModeOnPrintDesc: 'Reads only when Print button is pressed on scale',
    scaleReconnect: 'Reconnect',
    scaleReconnectStarted: 'Reconnect initiated',
    scaleStableWeight: 'Stable Weight (S)',

    requireQualityCheck: 'Require Quality Check',
    requireQualityCheckDesc: 'Mandate quality verification before completion',
    dataRetentionDays: 'Data Retention (days)',
    dataRetentionDaysDesc: 'How long to keep historical data before archiving',
    backupFrequency: 'Backup Frequency',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    maintenanceMode: 'Maintenance Mode',
    maintenanceModeDesc: 'Disable system access for maintenance',
    debugMode: 'Debug Mode',
    debugModeDesc: 'Enable detailed logging for troubleshooting',
    automaticBackups: 'Automatic Backups',
    automaticBackupsDesc: 'Enable automated database backups',
    warning: 'Warning',
    settingsWarning: 'Changing system settings may affect all users. Please ensure you understand the implications before saving.',
    unsavedChanges: 'You have unsaved changes',
    settingsSavedSuccess: 'Settings saved successfully',
    failedToSaveSettings: 'Failed to save settings',

    // Feature Flags
    featureFlags: 'Features',
    featureFlagsDesc: 'Enable or disable features globally for all users.',
    materialTypeRestriction: 'Material Type Restriction',
    materialTypeRestrictionDesc: 'Restrict stations to specific material types (Chicken, Beef, Pork, Goat/Lamb)',
    scannerIntegration: 'Scanner Integration',
    scannerIntegrationDesc: 'Enable barcode scanner features for scan verification',
    saveAndReload: 'Save & Reload',
    featureFlagsNote: 'Feature flags are stored in your browser. Changes will reload the page to take effect.',
    featureFlagsNoteGlobal: 'Feature flags are stored globally in the database and apply to all users. Changes take effect immediately.',
    featureFlagsSaved: 'Feature flags saved successfully',
    databaseToggles: 'Database Toggles',
    databaseTogglesDesc: 'These settings are stored in the database and take effect immediately for all users.',
    enableInputOutputEditing: 'Input/Output Mapping Editing',
    enableInputOutputEditingDesc: 'Allow supervisors to add new raw material linkages in the Input/Output Mapping tab',
    requirePostPrintScan: 'Post-Print Badge & Label Scan',
    requirePostPrintScanDesc: 'Require packer badge and box label double-scan after printing a label before recording production',

    // Production tab (Manager/Admin)
    productionTab: 'Production',
    yieldVarianceThresholds: 'Yield Variance Thresholds',
    yieldVarianceThresholdsDesc: 'Configure the variance thresholds used across the system for yield monitoring.',
    yieldThresholdLabel: 'Yield Variance Threshold',
    yieldThresholdDesc: 'Yield variance above this value triggers warnings, requires supervisor approval, and flags sub-sessions in reports.',
    yieldThresholdsSaved: 'Yield thresholds saved successfully',
    yieldThresholdsSaveFailed: 'Failed to save yield thresholds',

    // Weight Mode & Tolerance Settings
    defaultWeightMode: 'Default Weight Mode',
    defaultWeightModeDesc: 'Global default weight mode for packer view when no session-level override is set.',
    weightModeFixed: 'Fixed',
    weightModeCatch: 'Catch',
    weightModeScale: 'Scale',
    weightTolerance: 'Weight Tolerance',
    weightToleranceDesc: 'Maximum weight (in lbs) above the target unit weight that is accepted for a box.',
    weightSettingsSaved: 'Weight settings saved successfully',
    weightSettingsSaveFailed: 'Failed to save weight settings',
    autoTareTitle: 'Auto-Tare (Scale Mode)',
    autoTareDesc: 'Automatically subtract packaging weight from scale readings. Only applies when weight mode is Scale.',
    bagTare: 'Bag Tare',
    bagTareDesc: 'Weight of bag packaging to subtract during sub-unit packing.',
    caseTare: 'Case Tare',
    caseTareDesc: 'Weight of case/box packaging to subtract during final box packing.',
    tareGross: 'Gross',
    tareTare: 'Tare',
    tareNet: 'Net',

    // Production / Transfers (WCO-3061)
    productionTransfers: 'Production / Transfers',
    transferSourceWarehouseCode: 'Transfer-Back Source Warehouse',
    transferSourceWarehouseCodeDesc: 'Warehouse code used as the source (warehouse_code_from) on HTE transfer-back documents. Must be an HTE-family warehouse from the master list.',
    transferSourceWarehouseSaved: 'Transfer source warehouse saved successfully',
    transferSourceWarehouseSaveFailed: 'Failed to save transfer source warehouse',
    transferSourceWarehouseLoadFailed: 'Failed to load warehouse options',

    // Locations Page
    locationsAndWarehouses: 'Locations & Warehouses',
    manageProductionLocations: 'Manage production locations and connected warehouses',
    searchLocations: 'Search locations, addresses, or warehouses...',
    allStatus: 'All Status',
    capacity: 'Capacity',
    noLocationsFound: 'No locations found',
    getStartedCreatingLocation: 'Get started by creating a new location.',
    totalLocations: 'Total Locations',
    activeLocations: 'Active Locations',
    totalCapacity: 'Total Capacity',
    highCapacity: 'High Capacity',
    confirmDeleteLocation: 'Are you sure you want to delete location',
    locationCreatedSuccess: 'Location created successfully',
    failedToCreateLocation: 'Failed to create location',
    locationUpdatedSuccess: 'Location updated successfully',
    failedToUpdateLocation: 'Failed to update location',
    locationDeletedSuccess: 'Location deleted successfully',
    failedToDeleteLocation: 'Failed to delete location',
    locationStatusUpdated: 'Location status updated',
    failedToUpdateStatus: 'Failed to update status',

    // HTE Stock tab (Manager)
    hteStock: {
      title: 'HTE Stock',
      lotsTitle: 'Lot Stock',
      itemNumber: 'Item Number',
      itemName: 'Item Name',
      currentAmount: 'Current Amount (lbs)',
      receivedDate: 'Received',
      bufferTitle: 'Byproduct Marketable Buffer',
      byproduct: 'Byproduct',
      reservedWeight: 'Reserved Weight (lbs)',
      confirmPassword: 'Confirm Change',
      password: 'Password',
      reason: 'Reason',
      reasonPlaceholder: 'Why is this change needed?',
      updateConfirmMsg: 'Enter a reason and your password to confirm this update.',
      deleteConfirmMsg: 'Enter a reason and your password to confirm this deletion.',
      lotUpdated: 'Lot amount updated',
      bufferUpdated: 'Buffer weight updated',
      bufferDeleted: 'Buffer row deleted',
      updateFailed: 'Update failed',
      deleteFailed: 'Delete failed',
      invalidCredentials: 'Invalid credentials',
      invalidValue: 'Value must be a number >= 0',
    },
    packingMaterials: {
      title: 'Packing Materials',
      autoTransferSettings: 'Auto-Transfer Settings',
      autoTransferMode: 'Auto-Transfer Mode',
      modeDraft: 'Draft (Manager reviews)',
      modeAutoSubmit: 'Auto-Submit',
      modeDraftDesc: 'When materials are low, a draft transfer is created. You must review and submit it manually.',
      modeAutoSubmitDesc: 'When materials are low, a transfer is created and submitted automatically. You will be notified.',
      sourceLocation: 'Source Warehouse',
      sourceLocationDesc: 'Warehouse location where packing materials are ordered from.',
      autoTransferWhen: 'When',
      whenSameDay: 'Same Day',
      whenNextBusinessDay: 'Next Business Day',
      whenNextMonday: 'Next Monday',
      thresholds: 'Material Thresholds',
      noMaterials: 'No packing materials found in inventory. Add packing materials via the Product Mapping tab.',
      itemNumber: 'Item #',
      itemName: 'Item Name',
      currentStock: 'Current Stock',
      safeQuantity: 'Safe Qty',
      reorderQuantity: 'Reorder Qty',
      unitsAbbrev: 'u',
      thresholdSaved: 'Threshold saved',
      thresholdSaveFailed: 'Failed to save threshold',
      thresholdDeleted: 'Threshold removed',
      noThresholds: 'No material thresholds configured',
      addThresholdHint: 'Click (+) to add a packing material threshold',
      addMaterial: 'Add Packing Material',
      typeToSearch: 'Type to search materials...',
      settingSaved: 'Setting saved',
      packingUnit: 'Packing',
      pcs: 'pcs',
      draftTransferAlert: 'Packing material replenishment — review & submit',
      autoTransferCreatedTitle: 'Packing material transfer auto-created',
      autoTransferCreatedBody: 'Stock hit its safe level. Review and submit the draft transfer.',
      viewTransfer: 'View transfer',
      pendingTransferAlert: 'Packing material replenishment submitted',
      cancelTransferTitle: 'Cancel Transfer',
      confirmCancelTransfer: 'Cancel this draft transfer? This cannot be undone.',
      confirmCancel: 'Yes, Cancel',
      transferCancelled: 'Transfer cancelled',
    },
  },

  // Box Confirmation (double-scan)
  boxConfirm: {
    scanBadgePrompt: 'Scan your badge to confirm',
    scanLabelPrompt: 'Now scan the box label',
    confirmed: 'Box confirmed',
    badgeMismatch: 'Badge does not match assigned packer',
    labelNotFound: 'Box label not found',
    step1: 'Step 1: Scan Your Badge',
    step2: 'Step 2: Scan Box Label',
    waitingForBadge: 'Waiting for badge scan...',
    waitingForLabel: 'Waiting for label scan...',
    verifying: 'Verifying...',
    success: 'Box Confirmed!',
    badgeScanned: 'Badge scanned',
    scanBadgeFirst: 'Please scan your employee badge first',
    alreadyScannedBadge: 'Badge already scanned. Please scan the box label.',
    skipScan: 'Skip Scan',
    manualConfirmHint: 'Use if scanner not working',
    helpText: 'Scan your badge, then scan the printed label on the box to confirm',
  },

  // Material
  material: {
    initialWeight: 'Initial Weight',
    rawMaterial: 'Raw Material',
    brand: 'Brand',
    yieldFactor: 'Yield',
  },

  // Clock In
  clockIn: {
    title: 'Clock In',
    scanStation: 'Scan Station Barcode',
    scanStationHint: 'Point scanner at station barcode',
    scanBadge: 'Scan Your Badge',
    scanBadgeHint: 'Point scanner at your employee badge',
    selectRole: 'Select Your Role',
    operator: 'Operator',
    packer: 'Packer',
    welcome: 'Welcome',
    currentAssignments: 'Current Assignments',
    noActiveSession: 'No active session at this station',
    employeeNotFound: 'Employee not found',
    employeeDeactivated: 'Employee is deactivated',
    stationNotFound: 'Station not found',
    alreadyAssigned: 'You are already assigned as {role}',
    relieveConfirm: 'Relieve {name} as {role}?',
    relieveDescription: '{name} has been working since {time}',
    transferConfirm: 'Transfer Station?',
    transfer: 'Transfer',
    clockedIn: 'Scanned in successfully',
    clockingInAsPacker: 'Clocking in as Packer...',
    redirecting: 'Redirecting to production...',
    scanDifferentStation: 'Scan Different Station',
    skipToProduction: 'Skip to Production →',
    clockOut: 'Scan Out',
    clockOutConfirm: 'Scan out from {station}?',
    clockedOut: 'Scanned out successfully',
    orEnterManually: 'Or enter manually:',
  },

  // Mobile Views
  mobile: {
    // Scan Out
    scanOut: 'Scan Out',

    // Clock In
    scanStation: 'Scan Station',
    scanBadge: 'Scan Badge',
    selectRole: 'Select Role',
    confirmRelief: 'Confirm Relief',
    success: 'Success',
    scanStationBarcode: 'Scan Station Barcode',
    scanStationHint: 'Point scanner at station barcode',
    scanYourBadge: 'Scan Your Badge',
    scanBadgePlaceholder: 'Scan or enter badge...',
    scanBadgeAbove: 'Scan your badge',
    orEnterManually: 'Or enter manually:',
    noActiveSession: 'No active session at this station',
    sessionCompleted: 'This session has already been completed',
    sessionCancelled: 'This session has been cancelled',
    materialsNotReceived: 'Raw materials for sub-session {subSessionNumber} have not been received yet. Production can\'t start.',
    awaitingAssignment: 'Transfer for sub-session {subSessionNumber} has been received, but the sub-session is waiting for Operator/Packer assignment.',
    scheduledFuture: 'Sub-session {subSessionNumber} is scheduled for a future date and cannot be started yet.',
    currentAssignments: 'Currently Working',
    operator: 'Operator',
    packer: 'Packer',
    selectYourRole: 'Select Your Role',
    relieveWorker: 'Relieve Worker?',
    currentlyWorkingAs: 'is currently working as',
    yesRelieve: 'Yes, Relieve Them',
    clockedIn: 'Clocked In!',

    // Operator View
    operatorView: 'Operator View',
    confirmBackToStation: 'Going back to scan station?',
    clockOut: 'Scan Out',
    clockedOut: 'Scanned out successfully',
    clockedOutFromAnotherDevice: 'You have been clocked out from another device',
    loggedInFromAnotherDevice: 'You have logged in from another device',
    youHaveBeenRelieved: 'You have been relieved by another worker',
    sessionProgress: 'Session Progress',
    materialWeight: 'Input Material',
    remaining: 'Est. Remaining Output',
    pace: 'Pace',
    instructions: 'Instructions',
    operatorInstructions: 'Monitor the production line and ensure quality standards are met. Coordinate with packer for box weights.',
    qualityReminder: 'Check for foreign materials and ensure proper packaging before sealing.',

    // Packer View
    packerView: 'Packer View',
    boxes: 'Boxes',
    weightWindow: 'Weight Window',
    enterWeight: 'Enter box weight',
    weightValid: 'Weight is within range',
    weightTooLow: 'Weight is too low',
    weightTooHigh: 'Weight is too high',
    printLabel: 'Print Label',
    packSubUnit: 'Pack sub-unit',
    subUnit: 'Sub-unit',
    packed: 'packed',
    subUnitsPacked: 'Sub-units packed',
    nowPackTheBox: 'Now pack the box',
    labelPrinted: 'Label printed!',
    printerUnavailableBoxCounted: 'Printer unavailable — box counted',
    printerUnavailableCannotProceed: 'Printer unavailable — cannot print label. Please check printer connection.',
    confirmLabel: 'Confirm Label Applied',
    scanToConfirm: 'Scan badge then label to confirm',
    step1ScanBadge: '1. Scan Your Badge',
    step2ScanLabel: '2. Scan Box Label',
    waiting: 'Waiting...',
    verifying: 'Verifying...',
    scanBadgeFirst: 'Scan badge first',
    badgeScanned: 'Badge scanned',
    scanLabelNotBadge: 'Please scan the box label, not badge',
    boxConfirmed: 'Box Confirmed!',
    boxNumber: 'Box',
    scanBadgeForOptions: 'Scan your badge for options',
    scanStationForOptions: 'Scan station, then badge for options',
    nowScanBadge: 'Now scan your badge',
    stationScanned: 'Station scanned - now scan your badge',
    wrongStation: 'Wrong station - please scan the correct station',
    wrongBadge: 'Wrong badge - please scan your badge',
    stationBarcode: 'Station barcode...',
    badgeNumber: 'Badge number...',
    scanStationFirst: 'Please scan station first',
    whatWouldYouLikeToDo: 'What would you like to do?',
    backToPacker: 'Back to Packer',
    takeBreak: 'Take Break',
    pausing: 'Pausing...',
    breakStarted: 'Break started',
    differentBadge: 'Different badge scanned',
    supervisorOverrideActivated: 'Supervisor override activated',
    supervisorNoSession: 'No active session found',
    supervisorSessionError: 'Failed to load session data',
    supervisorBadgeError: 'Failed to verify badge',
    noOperatorAssigned: 'No operator assigned to this session yet',
    supervisor: 'Supervisor',
    supervisorOverride: 'Supervisor Override',
    scanSupervisorBadgeToContinue: 'Scan supervisor badge to continue',
    notASupervisor: 'Badge does not belong to a supervisor',
    badgeVerificationFailed: 'Badge verification failed',
    labelReprintsManualWeight: 'Label reprints, manual weight, etc.',
    clockInAsWorker: 'Clock In as Worker',
    workAsOperatorPacker: 'Work as operator or packer',
    verifyingBadge: 'Verifying badge...',
    wrongPackerBadge: 'Wrong badge - only the clocked-in packer can confirm',
    resumingPendingBox: 'Resuming previous box - scan badge to confirm',
    previousBoxCancelled: 'Previous unconfirmed box was voided',
    pendingLabelVoided: 'Pending label was voided by supervisor',
    boxCancelledCannotConfirm: 'This box has been voided and cannot be confirmed',
    packingInstructions: 'Packing Instructions',
    productionDetails: 'Production Details',
    itemNumber: 'Item #',
    englishName: 'English',
    chineseName: 'Chinese',
    rawMaterial: 'Raw Material',
    packingSpec: 'Packing',
    session: 'Session',
    progress: 'Progress',
    weightPerBox: 'Weight/Box',
    expectedBoxes: 'Expected Boxes',
    // Target Reached
    targetReached: 'Target Reached!',
    targetReachedMessage: "You've packed all the target boxes for this session.",
    continuePacking: 'Continue Packing',
    completeSession: 'Complete Session',
    finish: 'Finish',
    // Session Completed (by supervisor)
    sessionCompletedBySupervisor: 'Session Completed',
    sessionCompletedMessage: 'This session has been marked as completed by a supervisor. You can no longer pack boxes for this session.',
    returnToClockIn: 'Return to Station Scan',
    // Early Completion (End Session from badge menu)
    endSession: 'End Session',
    earlyCompletionWarning: 'Early Session Completion',
    earlyCompletionWarningDesc: 'Progress is less than 100% - Session will be marked as completed by override',
    earlyCompletionReason: 'Reason for Early Completion',
    selectReason: 'Select a reason...',
    selectEarlyCompletionReason: 'Please select a reason for early completion',
    selectReasonFirst: 'Select a reason above before scanning',
    reasonShiftEnded: 'Shift Ended',
    reasonMaterialShortage: 'Material Shortage',
    reasonEquipmentIssue: 'Equipment Issue',
    reasonOther: 'Other',
    // Waste Tracking Wizard
    wasteTracking: 'Waste Tracking',
    disposableWaste: 'Disposable Waste',
    disposableDesc: 'Trash, non-recoverable material',
    humanError: 'Human Error',
    humanErrorDesc: 'Does not count as waste',
    trackingOnly: 'Tracking only',
    returnsToInventory: 'Returns to Inventory',
    productionRemainder: 'Production Remainder',
    notWaste: 'Not waste - returns to lot',
    byProduct: 'By-Product',
    byProductDesc: 'Sellable or reusable by-product',
    byProductMarketable: 'Marketable',
    byProductMarketableDesc: 'can be sold',
    byProductProduction: 'Used for Production',
    byProductProductionDesc: 'reused in production',
    thisSession: 'This session',
    carryForward: 'Carry-forward',
    fromPriorSessions: 'from prior sessions',
    bufferTotal: 'Buffer total',
    readyCases: 'Ready cases',
    reserve: 'Reserve',
    addByproductEntry: 'Add by-product',
    removeEntry: 'Remove',
    remainderNotIntact: 'Remainder - Not Intact',
    remainderNotIntactDesc: 'Opened or partial raw materials',
    finishProductNotIntact: 'Finish Product - Not Intact',
    finishProductNotIntactDesc: "Remainder of finish product that doesn't make a full case",
    intactBoxes: 'Remainder - Intact',
    intactBoxesDesc: 'Sealed raw material boxes returned to lot',
    scanIntactBox: 'Scan intact box barcode',
    boxesScanned: 'boxes scanned',
    totalWeight: 'total lbs',
    removeBox: 'Remove',
    selectMaterial: 'Select Material',
    selectByproduct: 'Select by-product',
    noLinkedByproducts: 'No linked by-products',
    noByproductMappings: 'There are no by-products linked to the raw material used in this sub-session',
    enterWeightManually: 'Enter weight manually',
    manualAddCase: 'Manual Add Case',
    manualEntry: 'Manual Entry',
    scanBoxNotBadge: 'Scan a box barcode, not a badge',
    invalidBarcode: 'Invalid barcode - could not extract weight',
    boxAlreadyScanned: 'This box was already scanned',
    exceedsAllocatedWeight: 'Cannot scan: total would exceed allocated weight',
    gtinMismatch: 'Barcode does not match assigned raw materials',
    caseWeightMustBePositive: 'Case weight must be greater than 0',
    caseWeightAboveMax: 'Case weight exceeds the allowed maximum of {max} lbs',
    caseWeightOutOfRange: 'Case weight must be between {min} and {max} lbs',
    // Waste Summary
    waterLiquid: 'Water / Liquid Loss',
    waterLiquidAuto: 'Calculated automatically',
    wasteSummary: 'Waste Summary',
    yieldRate: 'Yield Rate',
    initialMaterial: 'Initial Material',
    totalOutput: 'Total Output',
    lbsCarryover: 'lbs carryover',
    carryover: 'Carryover',
    lbsFromSession: 'lbs from session',
    submitWaste: 'Submit',
    submittingWaste: 'Submitting waste tracking...',
    wasteSubmittedSuccess: 'Session Completed!',
    redirecting: 'Redirecting...',
    weightsExceedWarning: 'Entered weights exceed initial material',
    goBackAndCorrect: 'Go back and correct the values before submitting.',
    cannotSubmit: 'Cannot Submit',
    excessAmount: 'Excess',
    // Supervisor Approval
    yieldVarianceDetected: 'Yield Variance Detected',
    yieldReviewRequired: 'Yield Review Required',
    yieldWithinTolerance: 'Yield Within Tolerance',
    expectedYield: 'Expected Yield',
    actualYield: 'Actual Yield',
    scanSupervisorBadge: 'Scan supervisor badge to approve',
    scanSupervisorBadgeToComplete: 'Scan supervisor badge to complete session',
    supervisorApproval: 'Supervisor Approval Required',
    supervisorSignOff: 'Supervisor Sign-Off Required',
    supervisorApproved: 'Approved by',
    supervisorOnlyCanApprove: 'Only Supervisors can approve waste tracking',
    invalidBadge: 'Invalid badge',
    // Scale weight mode
    scaleWeight: 'Scale Weight',
    scaleWeightFromScale: 'Live reading from station scale',
    noScaleConfigured: 'No scale configured for this station',
    scaleNotConnected: 'Scale is not connected',
    waitingForStableWeight: 'Waiting for stable weight...',
    // Session summary
    sessionSummary: 'Session Summary',
    summaryTotalOutput: 'Total Output',
    totalWaste: 'Total Waste',
    inputWeight: 'Input Weight',
    outputWeight: 'Output Weight',
    waste: 'Waste',
    returns: 'Returns',
    weightDiscrepancy: 'Difference',
    accounted: 'Accounted',
    boxCount: 'Box Count',
    summaryPace: 'Pace',
    elapsedTime: 'Elapsed Time',
    summaryProgress: 'Progress',
    // Weight mode
    fixedWeight: 'Fixed Weight',
    catchWeight: 'Catch Weight',
    fixedWeightFromProduct: 'From product configuration',
    readyToPrint: 'Ready to print',
    readyToPackSubUnit: 'Ready to pack sub-unit',
  },

  // Session Details Modal
  sessionDetails: {
    title: 'Session Details',
    assignment: 'Assignment',
    details: 'Details',
    productInfo: 'Finish Product Information',
    scheduleInfo: 'Schedule Information',
    materialInfo: 'Raw Material Information',
    materialOnHoldAlert: 'Material awaiting manager approval — session cannot start until on-hold items are approved or rejected',
    existingInventory: 'from HTE inventory',
    inventoryTag: '(HTE)',
    allocated: 'Allocated',
    totalOnHold: 'Total on hold',
    assignStation: 'Assign Station',
    assignOperator: 'Assign Operator',
    splitSession: 'Split Session',
    stationAssignments: 'Station Assignments',
    subSessions: 'Sub-Sessions',
    selectStation: 'Select station...',
    selectOperator: 'Select operator...',
    noStationAssigned: 'No station assigned',
    noOperatorAssigned: 'No operator assigned',
    currentStation: 'Current station',
    currentOperator: 'Current operator',
    materialWeight: 'Weight (lbs)',
    minWeight: 'Min',
    alreadyUsed: 'Used',
    weightMode: 'Weight Mode',
    fixedWeight: 'Fixed',
    catchWeight: 'Catch',
    confirmSplit: 'Confirm Split',
    addStation: 'Add Station',
    station: 'Station',
    operator: 'Operator',
    totalWeight: 'Total Allocated',
    remaining: 'Remaining to Allocate',
    weightMismatch: 'Total weight must equal session material weight',
    minWeightWarning: '{{count}} sub-session(s) may not have enough weight to produce 1 box (min ~{{minWeight}} lbs)',
    stationAssigned: 'Station assigned successfully',
    // Station change modal (for active/paused)
    reassignStation: 'Change Station',
    reassignStationWarning: 'Workers will need to re-clock-in at the new station.',
    stationChangeReason: 'Reason for station change',
    selectStationChangeReason: 'Select reason...',
    stationChangeReasons: {
      equipment_issue: 'Equipment Issue',
      workload_balance: 'Workload Balance',
      operator_request: 'Operator Request',
      supervisor_decision: 'Supervisor Decision',
      station_maintenance: 'Station Maintenance',
      other: 'Other',
    },
    stationChangeOtherPlaceholder: 'Enter reason...',
    confirmStationChange: 'Change Station',
    // Add material modal
    addMaterial: 'Add Material',
    inputMode: 'Input Mode',
    byWeight: 'By Weight',
    byBox: 'By Box',
    selectLot: 'Select Material',
    weightToAdd: 'Weight to Add (lbs)',
    targetBoxes: 'Target Boxes',
    requiredWeight: 'Required weight',
    availableWeight: 'Available',
    estBoxesPrefix: 'Est.',
    boxesUnit: 'boxes',
    insufficientStock: 'Insufficient stock available',
    // WCO-2607: per-lot picker
    selectLotsToAdd: 'Select lots to add',
    perLotWeight: 'Weight (lbs)',
    lotCapExceeded: 'Cannot exceed the lot amount',
    lotIntact: 'Intact',
    lotNotIntact: 'Not-intact',
    lotByProduct: 'By-product',
    lotOnHold: 'On-hold',
    lotTransfer: 'Transfer',
    carriedToSession: '−{{cases}} cases / −{{weight}} lbs carried to session {{session}}',
    carriedToSessionPartial: '−{{weight}} lbs (partial) carried to session {{session}}',
    carriedToSessionWeightOnly: '−{{weight}} lbs carried to session {{session}}',
    carriedFromSession: '+{{cases}} cases / +{{weight}} lbs carried from session {{session}}',
    carriedFromSessionPartial: '+{{weight}} lbs (partial) carried from session {{session}}',
    carriedFromSessionWeightOnly: '+{{weight}} lbs carried from session {{session}}',
    // WCO-2659: frozen "remaining material" line (weight-first, cases-as-qualifier).
    remainderProduced: '{{weight}} lbs remaining material ({{breakdown}})',
    remainderProducedIntact: '{{weight}} lbs / {{cases}} cases remaining material',
    remainderProducedPartial: '{{weight}} lbs (partial) remaining material',
    remainderProducedWeightOnly: '{{weight}} lbs remaining material',
    remainderProducedIntactPart: '{{cases}} cases intact',
    remainderProducedIntactWeightPart: 'intact',
    remainderProducedPartialPart: '{{weight}} partial',
    lotCases: '{{n}} cases',
    estUsable: 'Est. Usable (lbs)',
    noLotsAvailable: 'No usable lots available for this material',
    total: 'Total',
    lotsUnit: 'lots',
    casesUnit: 'cases',
    estOutputBoxes: 'output boxes',
    willDistribute: 'Will distribute to existing sub-sessions:',
    willAddToExisting: 'Will add to existing sub-session:',
    willCreateNew: 'Will create new sub-session:',
    selectTargetSubSession: 'Select Target Sub-Session',
    selectFinishProductSubSession: 'Select sub-session...',
    selectTargetSubSessions: 'Select Target Sub-Session(s)',
    selectNoneForNew: 'Deselect all to create a new sub-session instead',
    addMaterialReason: 'Reason',
    selectAddMaterialReason: 'Select reason...',
    addMaterialReasons: {
      insufficient_material: 'Insufficient Material',
      production_increase: 'Production Increase',
      quality_issue: 'Quality Issue',
      supervisor_decision: 'Supervisor Decision',
      leftover_previous_session: 'Leftover from previous session',
      other: 'Other',
    },
    addMaterialOtherPlaceholder: 'Enter reason...',
    addMaterialTabRawMaterial: 'Raw Material',
    addMaterialTabFinishProduct: 'Finish Product',
    selectProduct: 'Select Product',
    addProduct: 'Add Product',
    confirmAddMaterial: 'Add Material',
    materialDistributed: 'Material added and distributed to existing sub-sessions',
    newSubSessionCreated: 'Material added as new sub-session',
    materialAddedToExisting: '{{weight}} lbs will be added to existing sub-session (save to apply)',
    materialAddedAsNew: '{{weight}} lbs will create a new sub-session (save to apply)',
    finishProductAdded: 'Finish product allocated to session',
    // Subdivide modal
    subdivide: 'Subdivide',
    subdivideSession: 'Subdivide Sub-Session',
    subdivideDesc: 'Split remaining unprocessed weight to a new sub-session.',
    remainingWeight: 'Remaining',
    minPerSubSession: 'Min per sub-session',
    splitWeight: 'Weight to Split (lbs)',
    invalidSplitWeight: 'Each sub-session must have enough weight for at least 1 box',
    targetStation: 'Station for New Sub-Session',
    assignLater: 'Assign later',
    subdivideReason: 'Reason',
    selectSubdivideReason: 'Select reason...',
    subdivideReasons: {
      workload_balance: 'Workload Balance',
      speed_up_production: 'Speed Up Production',
      operator_availability: 'Operator Availability',
      station_availability: 'Station Availability',
      other: 'Other',
    },
    subdivideOtherPlaceholder: 'Enter reason...',
    confirmSubdivide: 'Subdivide',
    subdivideSuccess: 'Successfully split weight to new sub-session',
    subdivideStaged: 'Split staged. Click Save to apply.',
    cancelSubSession: 'Cancel Sub-Session',
    cancelSubSessionDesc: 'Are you sure you want to cancel this sub-session? The material weight will be returned to the lot.',
    confirmCancelSubSession: 'Are you sure you want to cancel this sub-session? The material weight will be returned to the lot.',
    subSessionCancelled: 'Sub-session cancelled',
    splitWeightTooLarge: 'Split weight must be less than original weight',
    unsavedChanges: 'Unsaved Changes',
    unsavedChangesMessage: 'You have unsaved changes. Are you sure you want to close without saving?',
    discardChanges: 'Discard Changes',
    // Station conflict (priority queue) modal
    stationConflictTitle: 'Station Already Active',
    stationConflictMessage: 'Station {station} has an active sub-session ({subSession}).',
    stationQueueExists: 'There are {count} sub-sessions already queued.',
    stationPriorityQueue: 'Your sub-session will have PRIORITY and be first in queue.',
    stationWillActivate: 'Once the current active sub-session completes, yours will start.',
    confirmQueuePriority: 'Queue with Priority',
    priorityBadge: 'Priority',
    queuedWithPriority: 'Sub-session queued with priority',
    invalidWeight: 'Please enter a valid weight',
    operatorAssigned: 'Operator assigned successfully',
    sessionSplit: 'Session split into sub-sessions successfully',
    sessionUpdated: 'Session updated successfully',
    failedToAssignStation: 'Failed to assign station',
    failedToAssignOperator: 'Failed to assign operator',
    cannotClearOperator: 'Cannot remove operator — an operator is required for the session to run',
    cannotClearPacker: 'Cannot remove packer — a packer is required for the session to run',
    assignPacker: 'Assign Packer',
    selectPacker: 'Select packer...',
    currentPacker: 'Current packer',
    noPackerAssigned: 'No packer assigned',
    packerAssigned: 'Packer assigned successfully',
    failedToAssignPacker: 'Failed to assign packer',
    failedToSplitSession: 'Failed to split session',
    cannotAddToMultiMaterial: 'Cannot add sub-sessions to multi-material sessions',
    cannotSplitStarted: 'Cannot split a session that has already started',
    startSplit: 'Split into multiple stations',
    splitDescription: 'Allocate material weight to multiple stations. Total must equal session weight',
    splitByMaterialDescription: 'Assign each material to a station. Select a station for each material below.',
    splitHint: 'Split this session across multiple stations for parallel processing',
    packing: 'Packing',
    initialWeight: 'Initial Weight',
    initialWeightTooltip: 'Raw material weight allocated to this session',
    unitWeight: 'Unit Weight',
    yieldFactor: 'Yield Factor',
    estimatedOutput: 'Estimated Output',
    carryover: 'carryover',
    estimatedCases: 'Estimated Cases',
    actualCases: 'Actual Cases',
    perSubSessionCases: 'Cases by Sub-Session',
    expectedCasesShort: 'Expected',
    actualCasesShort: 'Actual',
    perOutputLocationCases: 'Cases by output location',
    outputLocation: 'Location',
    noStationYet: 'No station yet',
    currentProgress: 'Current Progress',
    cases: 'cases',
    timestamps: 'Timestamps',
    completedAt: 'Completed',
    productionResults: 'Production Results',
    actualOutput: 'Actual Output',
    wasteWeight: 'Waste',
    notes: 'Notes',
    createdBy: 'Created By',
    createdAt: 'Created At',
    date: 'Date',
    dateCode: 'Date Code',
    brand: 'Brand',
    lotNumber: 'Lot Number',
    location: 'Location',
    transferBackLocation: 'Transfer Back Location(s)',
    // Session control
    startSession: 'Start',
    pauseSession: 'Pause',
    resumeSession: 'Resume',
    cancelSession: 'Cancel Session',
    sessionStarted: 'Session started successfully',
    sessionPaused: 'Session paused successfully',
    sessionResumed: 'Session resumed successfully',
    sessionCancelled: 'Session cancelled successfully',
    failedToStart: 'Failed to start session',
    failedToPause: 'Failed to pause session',
    failedToResume: 'Failed to resume session',
    failedToCancel: 'Failed to cancel session',
    vb6TransferReminder: 'Remember to delete transfer in VB6',
    vb6TransferDetails: 'Transfer',
    pauseSessionDesc: 'Select a reason for pausing this session.',
    cancelSessionDesc: 'Please provide a reason for cancelling this session.',
    cancelActiveWarning: 'Warning: This session is currently active. Cancelling will release the station and any unsaved progress will be lost.',
    cancelReasonPlaceholder: 'Enter cancellation reason...',
    // Cancel reason dropdown (WCO-2706)
    selectCancelReason: 'Select a reason...',
    cancelReasons: {
      wrong_product: 'Wrong product',
      duplicate: 'Duplicate session',
      entered_in_error: 'Entered in error',
      other: 'Other',
    },
    provideCancelReason: 'Please provide a cancellation reason',
    sessionNotCancellable: 'This session can no longer be cancelled because its status is "{{status}}". Only sessions that have not started yet can be cancelled.',
    confirmPause: 'Confirm Pause',
    confirmCancel: 'Confirm Cancel',
    // History tab
    history: 'History',
    boxes: 'Cases',
    overrides: 'Overrides',
    activityLog: 'Activity Log',
    packedBoxes: 'Packed Cases',
    boxNumber: 'Case #',
    barcode: 'Barcode',
    weight: 'Weight',
    packedAt: 'Packed At',
    updatedAt: 'Updated At',
    packedBy: 'Packed By',
    updatedBy: 'Updated By',
    noHistory: 'No activity history found',
    noBoxes: 'No boxes packed yet',
    noCases: 'No cases packed yet',
    by: 'By',
    subsession: 'Subsession',
    noSubsession: 'No Subsession',
    scanIn: 'Scan In',
    scanOut: 'Scan Out',
    breakStart: 'Break Started',
    breakEnd: 'Break Ended',
    assigned: 'Assigned',
    unassigned: 'Unassigned',
    reassigned: 'Reassigned',
    historyCreated: 'Created',
    historySubmitted: 'Submitted',
    historyReceived: 'Material Received',
    historyReady: 'Ready',
    historyStarted: 'Started',
    historyCompleted: 'Completed',
    historyCancelled: 'Cancelled',
    historyPaused: 'Paused',
    historyResumed: 'Resumed',
    historyReprintsResolved: 'Reprints Acknowledged',
    historySubdivided: 'Subdivided',
    historyMaterialAdded: 'Material Added',
    historyFinishProductAdded: 'Finish Product Added',
    historyTransferReminderCreated: 'Transfer Reminder Created',
    historyTransferReminderResolved: 'Transfer Reminder Resolved',
    historyEdited: 'Edited',
    historyStatusChanged: 'Status Changed',
    historyStationAssigned: 'Station Assigned',
    historyStationUnassigned: 'Station Unassigned',
    historyOperatorAssigned: 'Operator Assigned',
    historyPackerAssigned: 'Packer Assigned',
    historyWeightModeChanged: 'Weight Mode Changed',
    boxPacked: 'Box Packed',
    boxCancelled: 'Box Cancelled',
    box: 'Box',
    packer: 'Packer',
    override_manual_weight: 'Weight Mode Changed',
    override_label_reprint: 'Label Reprinted',
    override_yield_signoff: 'Yield Sign-off',
    override_session_completion: 'Session Completion Override',
    override_eod_signoff: 'EOD Sign-off Override',
    materialIssue: 'Raw Material Issue',
    materialIssueDamagedWeight: 'Damaged Weight',
    materialIssueItem: 'Material',
    materialIssueLot: 'Lot',
    materialIssueInputWeight: 'Input Weight',
    materialIssueTargetOutput: 'Target Output',
    materialIssueReportedBy: 'Reported By',
    materialIssueMethod: 'Method',
    materialIssueScan: 'Barcode Scan',
    materialIssueManual: 'Manual Entry',
    // Material Added event
    materialAddedItem: 'Material',
    materialAddedLot: 'Lot',
    materialAddedWeight: 'Weight',
    materialAddedReason: 'Reason',
    materialAddedNote: 'Note',
    // Transfer Verified event
    transferVerified: 'Transfer Verified',
    materialReceived: 'Material Received',
    approved: 'Approved',
    received: 'Received',
    onHold: 'On Hold',
    rejected: 'Rejected',
    // Weight Modified event
    weightSetTo: 'Weight set to',
    // Subdivided event
    splitOff: 'split off',
    remainingAfterSplit: 'remaining',
    progressAtEvent: 'Progress',
    boxesPackedDuringShift: 'Packed this shift',
    packerSummary: 'Packer Summary',
    employeeSummary: 'Employee Summary',
    totalSessionTime: 'Total session time',
    timeActive: 'Time active',
    rolePacker: 'Packer',
    roleOperator: 'Operator',
    showMoreCancelled: 'Show {{count}} more cancelled',
    hideCancelled: 'Hide cancelled',
    // Early Completion (from supervisor dashboard)
    completeSession: 'Complete',
    sessionCompleted: 'Session completed',
    earlyCompletionWarning: 'Early Session Completion',
    earlyCompletionWarningDesc: 'This session has not reached its target. It will be marked as completed by supervisor override.',
    wasteTrackingReminder: 'Note: Ensure waste tracking has been completed at the station before closing this session.',
    completionReason: 'Reason for Early Completion',
    selectReason: 'Select a reason...',
    selectCompletionReason: 'Please select a reason',
    reasonShiftEnded: 'Shift Ended',
    reasonMaterialShortage: 'Material Shortage',
    reasonEquipmentIssue: 'Equipment Issue',
    reasonOther: 'Other',
    confirmComplete: 'Complete Session',
    // Source Location
    sourceLocation: 'Source Location',
    inboundTransferFrom: 'Inbound transfer from',
    sourceLocationDescription: 'Select a source location to auto-create an inbound transfer for non-on-hold materials.',
    selectSourceLocation: 'Select source location...',
    cannotEditActiveSession: 'Cannot modify active/completed sessions',
    viewTransfer: 'View Transfer',
    // PDF actions
    printPdf: 'Print',
    downloadPdf: 'Download PDF',
    pdfError: 'Failed to generate PDF',
    // Status dropdown
    statusDropdown: {
      next: 'Next (Promote to Ready)',
      promoteSuccess: 'Sub-session promoted to ready',
      statusChanged: 'Status changed successfully',
      demoteToPlanned: 'Back to Submitted',
      promoteToReady: 'Promote to Ready',
    },
  },
  supervisorOverride: {
    title: 'Supervisor Override',
    // Tabs
    labelReprints: 'Label Reprints',
    manualWeightMode: 'Manual Weight Mode',
    endOfDaySummary: 'End of Day Summary',
    // Label reprints
    reprintButton: 'Reprint',
    reprinting: 'Reprinting...',
    reprintReason: 'Reason for Reprint',
    reprintSuccess: 'Label sent to printer',
    reprintPrintFailed: 'Print failed',
    printerUnavailable: 'Printer unavailable — please check printer connection',
    viewBarcode: 'View Barcode',
    barcodeInfo: 'Barcode Information',
    noBarcodeAvailable: 'No barcode available',
    reasons: {
      damaged: 'Damaged Label',
      misprint: 'Misprint',
      illegible: 'Illegible',
      wrongInfo: 'Wrong Information',
      other: 'Other',
    },
    // Manual weight mode
    selectWeightMode: 'Select Weight Mode',
    enableCatchWeight: 'Enable catch weight entry',
    manualModeActive: 'Manual weight entry is ACTIVE while this panel is open',
    manualModeDescription: 'Weight input will accept manual entry instead of requiring scale readings. This mode deactivates when the override panel is closed.',
    activateManualMode: 'Enable Catch Weight',
    manualModeReason: 'Reason for Manual Mode',
    manualModeRecorded: 'Manual weight mode override recorded',
    weightModeChanged: 'Weight mode changed',
    weightModeChangeFailed: 'Failed to change weight mode',
    // End of day summary
    inputWeight: 'Input Weight',
    outputWeight: 'Output Weight',
    waste: 'Waste',
    yieldPercent: 'Yield %',
    expectedYield: 'Expected Yield',
    actualYield: 'Actual Yield',
    variance: 'Variance',
    boxCount: 'Box Count',
    avgBoxWeight: 'Avg Box Weight',
    pace: 'Pace',
    elapsedTime: 'Elapsed Time',
    progress: 'Progress',
    reviewAndSignOff: 'Review yield and sign off',
    signOff: 'Sign Off',
    signOffSuccess: 'Yield sign-off recorded',
    signedOff: 'Signed Off',
    pendingSignOff: 'Pending Sign-Off',
    // Status messages
    badgeInvalid: 'Not a supervisor badge',
    overrideRecorded: 'Override recorded successfully',
    noActiveSession: 'No active session to override',
    selectReason: 'Please select a reason',
    // Void label
    voidLabel: 'Void Label',
    confirmVoid: 'Void',
    voidSuccess: 'Label voided successfully',
    voidFailed: 'Failed to void label',
  },

  // Material Issue
  materialIssue: {
    menuButton: 'Raw Material Issue',
    title: 'Raw Material Issue',
    description: 'Report damaged raw material found during production',
    scanBarcode: 'Scan box',
    scanBarcodeHint: 'Scan the barcode on the damaged raw material box',
    manualWeight: 'Enter weight',
    weightLabel: 'Damaged Weight (lbs)',
    weightPlaceholder: 'Enter weight in lbs',
    reasonLabel: 'Reason for rejection',
    reasonPlaceholder: 'Describe why this material is being rejected...',
    barcodeDetected: 'Barcode detected',
    materialMatched: 'Material matched',
    materialName: 'Material',
    transferNumber: 'Transfer #',
    weightExtracted: 'Weight from barcode',
    impactPreview: 'Impact Preview',
    currentInputWeight: 'Current Input Weight',
    newInputWeight: 'New Input Weight',
    currentTargetOutput: 'Current Target Output',
    newTargetOutput: 'New Target Output',
    currentExpectedBoxes: 'Current Expected Boxes',
    newExpectedBoxes: 'New Expected Boxes',
    submit: 'Submit Material Issue',
    submitting: 'Submitting...',
    success: 'Material issue recorded successfully',
    error: 'Failed to record material issue',
    previousIssues: 'Previous Issues',
    noPreviousIssues: 'No previous material issues for this session',
    weightExceedsAvailable: 'Weight exceeds available material',
    barcodeNotMatched: 'Barcode does not match any material in this session',
    scanOrManual: 'Scan barcode or enter weight manually',
    lbs: 'lbs',
    reasonSelectPlaceholder: 'Select a reason...',
    reasonWrongMaterial: 'Wrong material',
    reasonWrongTemperature: 'Wrong temperature',
    reasonMaterialDamaged: 'Material damaged',
    reasonMaterialExpired: 'Material expired',
  },

  // Transfer Verification
  transferVerification: {
    title: 'Transfers',
    subtitle: 'Receive and verify incoming raw material transfers',
    item: 'Item',
    scanBadge: 'Scan Supervisor Badge',
    viewAllTransfers: 'View All Transfers',
    scanBadgeSubtitle: 'Authenticate to begin verification',
    pendingTransfers: 'Pending Transfers',
    noTransfersPending: 'No transfers pending verification',
    itemsPending: 'items pending',
    startVerification: 'Start Verification',
    expectedItems: 'Expected Items',
    scannedItems: 'Scanned Items',
    expected: 'Expected',
    scanned: 'Scanned',
    actual: 'Actual',
    variance: 'Variance',
    boxes: 'Boxes',
    approve: 'Approve',
    reject: 'Reject',
    approved: 'Approved',
    rejected: 'Rejected',
    approveConfirm: 'Confirm that all items match the transfer?',
    rejectConfirm: 'Reject this item? This cannot be undone.',
    confirmSubmit: 'Confirm Verification',
    confirm: 'Confirm',
    notesRequired: 'Please provide notes explaining the discrepancy',
    notesPlaceholder: 'Describe what was found (e.g., 2 boxes damaged, wrong material...)',
    notes: 'Notes',
    rejectReason: 'Reason for rejection',
    selectRejectReason: 'Select reason for rejection',
    qualityIssue: 'Quality Issue',
    temperatureIssue: 'Temperature Issue',
    boxNotIntact: 'Box not intact',
    wrongItem: 'Wrong item',
    wrongMaterial: 'Wrong material',
    changeItemNumber: 'Corrected Item Number',
    leaveBlankIfCorrect: 'Leave blank if item is correct',
    actualQty: 'Actual Quantity',
    actualWeight: 'Actual Weight (lbs)',
    verificationComplete: 'Transfer verification complete',
    boxScanned: 'Box scanned',
    scanBoxes: 'Scan raw material boxes',
    scanBoxBarcode: 'Scan a box barcode (GS1-128)',
    waitingForScan: '[ ] Scan a box barcode',
    badgeNotBox: 'Badge scanned — scan a box barcode instead',
    invalidBarcode: 'Invalid barcode format',
    proceedToDecision: 'Proceed to Review',
    perItemDecision: 'Review Items',
    submitAll: 'Submit Verification',
    submitError: 'Failed to submit verification',
    checkpointSaveError: 'Failed to save scan progress — your progress may be lost if you navigate away',
    loadError: 'Failed to load transfers',
    boxesMatch: 'Boxes match',
    boxesMismatch: 'Box count mismatch',
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    weeksAgo: 'weeks ago',
    scanTransferBarcode: 'Scan transfer barcode or enter number',
    enterTransferNumber: 'Transfer number...',
    transferNotFound: 'Transfer not found',
    transferLookupError: 'Error looking up transfer',
    verifyTab: 'Verify',
    historyTab: 'History',
    noHistory: 'No verification history yet',
    historyLoadError: 'Failed to load verification history',
    transfer: 'Transfer',
    received: 'Received',
    itemNotInTransfer: 'This item is not part of this transfer',
    noTransferSelected: 'No transfer selected',
    decidePendingFirst: 'Accept or reject the current box first',
    accept: 'Accept',
    accepted: 'Accepted',
    unknownBarcode: 'Barcode not found in system',
    badWeight: 'Could not read a valid weight from this label. Re-scan the box, or use Manual Add (Damaged Label).',
    invalidBoxWeight: 'A box has an invalid weight. Re-scan or reject it before submitting:',
    weightMissing: 'no weight recorded',
    weightUnreadable: 'unreadable weight',
    notInTransfer: 'Not in transfer',
    notInTransferWarning: 'This item is NOT in this transfer order',
    itemsNotInTransfer: 'Items not in this transfer',
    perItemMode: 'Per Item',
    wholeTransferMode: 'Whole Transfer',
    itemsBreakdown: 'Items breakdown',
    alreadyVerified: 'This transfer has already been verified',
    readyToSubmit: 'Ready to submit',
    enterActualWeight: 'Enter actual weight',
    manualAdd: 'Manual Add (Damaged Label)',
    manualAddTitle: 'Manual Entry',
    selectItem: 'Select item:',
    enterWeight: 'Weight (lbs)',
    qty: 'qty',
    manualAddReason: 'Reason for manual entry:',
    damagedLabel: 'Damaged Label',
    missingLabel: 'Missing Label',
    labelUnreadable: 'Label Unreadable',
    manual: 'Manual',
    boxDetails: 'Box details',
    hold: 'Hold',
    onHold: 'On Hold',
    holdReason: 'Reason for hold',
    selectHoldReason: 'Select reason for hold',
    holdReasonRequired: 'Please select a reason for putting this item on hold',
    onHoldReasons: {
      temperature: 'Temperature issue',
      wrongItemTomorrow: "Wrong item (tomorrow's production)",
      other: 'Other',
    },
    scanSummary: 'Scan Summary',
    scanSummarySubtitle: 'Review scanned vs expected totals before proceeding',
    totalWeight: 'Total Weight',
    discrepancyWarning: 'Discrepancy detected — please review before confirming',
    confirmProceed: 'Confirm & Proceed',
    goBackToScanning: 'Go Back',
    noDiscrepancies: 'All totals match',
    notInTransferBoxes: 'Boxes not in transfer',
    resumeTitle: 'Resume Previous Session?',
    resumeMessage: 'Found {count} previously scanned boxes by {name}. Resume or start fresh?',
    resume: 'Resume',
    startFresh: 'Start Fresh',
    otherSupervisorWarning: '{name} is currently receiving this transfer. Take over?',
  },

  // Manager Approval Queue
  managerApproval: {
    title: 'Approval Queue',
    subtitle: 'Review and approve on-hold items',
    itemsPending: 'item(s) pending approval',
    noPendingItems: 'No items pending approval',
    pendingApproval: 'Pending Approval',
    itemNumber: 'Item Number',
    itemName: 'Item Name',
    transferNumber: 'Transfer #',
    quantity: 'Quantity',
    weight: 'Weight',
    holdReason: 'Hold Reason',
    receivedBy: 'Received By',
    receivedAt: 'Received At',
    notes: 'Notes',
    approve: 'Approve',
    reject: 'Reject',
    approveConfirm: 'Approve this item for production?',
    rejectConfirm: 'Reject this item and return to warehouse?',
    approvalNotes: 'Approval notes (optional)',
    rejectionNotes: 'Rejection notes (required)',
    rejectionNotesRequired: 'Please provide a reason for rejection',
    approved: 'Item approved successfully',
    rejected: 'Item rejected successfully',
    approveError: 'Failed to approve item',
    rejectError: 'Failed to reject item',
    loadError: 'Failed to load pending approvals',
  },

  // Reports
  reports: {
    title: 'Reports',
  },

  adminActivity: {
    title: 'Admin Activity',
    description: 'Audit log of admin configuration changes — settings, users, stations, cameras, and mappings.',
    filters: 'Filters',
    clearFilters: 'Clear filters',
    entityFilter: 'Entity type',
    allEntities: 'All entities',
    actorFilter: 'Actor',
    actorPlaceholder: 'Search by user ID or name',
    dateFrom: 'From date',
    dateTo: 'To date',
    selectDate: 'Select date…',
    stateChanges: 'Changes',
    noFieldChanges: 'No field-level changes to display.',
    when: 'When',
    who: 'Who',
    entity: 'Entity',
    entityId: 'Entity ID',
    action: 'Action',
    details: 'Details',
    before: 'Before',
    after: 'After',
    showDetails: 'Show changes',
    hideDetails: 'Hide changes',
    noRecords: 'No audit records found',
    loadError: 'Could not load admin activity. You may not have permission or the server returned an error.',
    recordCount: '{count} record(s)',
    pageOf: 'Page {page} of {pages}',
    entities: {
      config_setting: 'Config Setting',
      packing_threshold: 'Packing Threshold',
      user: 'User',
      station: 'Station',
      camera: 'Camera',
      byproduct_mapping: 'By-Product Mapping',
      input_output_mapping: 'Input/Output Mapping',
      packing_mapping: 'Packing Mapping',
    },
    fields: {
      user_id: 'User',
      email: 'Email',
      user_level: 'Role',
      employee_number: 'Employee #',
      disabled: 'Disabled',
      setting_key: 'Setting key',
      setting_value: 'Value',
      description: 'Description',
      updated_by: 'Updated by',
      item_number: 'Item #',
      safe_quantity: 'Safe quantity',
      reorder_quantity: 'Reorder quantity',
      station_id: 'Station ID',
      station_name: 'Station name',
      station_type: 'Station type',
      is_active: 'Active',
      camera_id: 'Camera ID',
      camera_name: 'Camera name',
      ip_address: 'IP address',
      product_item_number: 'Product item #',
      packing_item_number: 'Packing item #',
      packing_level: 'Packing level',
      notes: 'Notes',
      warehouse_code: 'Warehouse',
      location_code: 'Location',
      status: 'Status',
      camera_url: 'Camera URL',
      camera_type: 'Camera type',
      position: 'Position',
      resolution: 'Resolution',
      fps: 'Frames per second',
      unifi_camera_id: 'UniFi camera ID',
      created_by: 'Created by',
      id: 'ID',
      raw_material_item_number: 'Raw material item #',
      byproduct_item_number: 'By-product item #',
      input_item_id: 'Input group ID',
      input_item_number: 'Input item #',
      preference: 'Preference',
      quantity_per_box: 'Quantity per box',
      passwordReset: 'Password reset',
      passwordChanged: 'Password changed',
    },
    actions: {
      CREATE: 'Create',
      UPDATE: 'Update',
      DELETE: 'Delete',
      TOGGLE_STATUS: 'Toggle Status',
      PASSWORD_RESET: 'Password Reset',
    },
  },

  // End of Day Summary
  endOfDaySummary: {
    title: 'End of Day Summary',
    selectDate: 'Select Date',
    today: 'Today',
    previousDay: 'Previous Day',
    nextDay: 'Next Day',

    // Summary cards
    totalSessions: 'Total Sessions',
    completedSessions: 'Completed',
    totalBoxes: 'Boxes Packed',
    voided: 'voided',
    totalWeight: 'Total Weight',
    waste: 'waste',
    avgYield: 'Average Yield',
    onTarget: 'On Target',
    belowTarget: 'Below Target',
    ofMaterial: 'of material',

    // Tabs
    byProduct: 'Products',
    byStation: 'Stations',
    byWorker: 'Operators',
    allSessions: 'All Sessions',
    wasteLog: 'Waste Log',

    // Waste table
    session: 'Session',
    product: 'Product',
    output: 'Output',
    wastePercent: 'Waste %',
    totalWaste: 'Total Waste',
    noWasteLogged: 'No waste logged for this date',
    wasteBreakdown: 'Waste Breakdown by Type',
    wasteBySession: 'Waste by Session',

    // Table headers
    productSku: 'SKU',
    productItemNumber: 'Item Number',
    productName: 'Product',
    stationName: 'Station',
    location: 'Location',
    workerName: 'Worker',
    employeeNumber: 'Employee #',
    sessionsCount: 'Sessions',
    boxesPacked: 'Boxes',
    weight: 'Weight',
    yield: 'Yield',
    pace: 'Pace',
    paceUnit: 'lb/hr',
    activeHours: 'Active Hours',
    status: 'Status',
    sessionNumber: 'Session #',
    operators: 'Operators',
    topPerformer: 'Top Performer',
    createdAt: 'Created At',
    completedAt: 'Completed At',

    // Alerts
    alerts: 'Alerts',
    noAlerts: 'No issues detected',
    alertLowYield: 'Session {{session}} has low yield ({{pct}}%)',
    alertHighVoidRate: 'Session {{session}} has high void rate ({{pct}}%)',
    alertIncompleteSessions: '{{count}} session(s) are still incomplete',
    alertByproductAging: '{{item}} ({{lbs}} lbs) - {{days}} days old',
    lowYield: 'Low yield',
    highVoidRate: 'High void rate',
    incompleteSessions: 'Incomplete sessions',
    viewSession: 'View',

    // Export
    export: 'Export',
    print: 'Print',
    downloadCsv: 'Download CSV',
    downloadPdf: 'Download PDF',
    pdfDownloadError: 'Failed to download PDF',

    // Material Tracking
    rawMaterial: 'Raw Material',
    materialTracking: 'Material Tracking',
    initialRawMaterial: 'Initial Raw Material',
    finalProduct: 'Final Product',
    actualWaste: 'Actual Waste',
    returnsAndByProducts: 'Returns & By-Products',
    materialUtilization: 'Material Utilization',
    ofInitialMaterial: 'of initial material',
    byMaterial: 'By Material',
    allocated: 'Allocated',
    remainder: 'Remainder',
    used: 'Used',
    noMaterialData: 'No material data for this date',
    noMaterialLotData: 'No material lot data available',
    returns: 'Returns',
    byProducts: 'By-Products',

    // Empty states
    noData: 'No production data for this date',
    noSessions: 'No sessions found',
    errorLoading: 'Failed to load summary data',

    // Transfer Out Summary
    transferOutSummary: 'Transfer Out Summary',
    noTransferData: 'No transfer data for this date',
    expQty: 'Exp. Qty',
    actQty: 'Act. Qty',
    expWeight: 'Exp. Weight',
    actWeight: 'Act. Weight',
    rawMaterialsKpi: 'Raw Materials',
    expected: 'Expected',
    received: 'Received',
    totalOutput: 'Total Output',
    efficiency: 'Efficiency',
    onHold: 'On Hold',
  },

  // Override History (Mobile)
  overrideHistory: {
    labelReprint: 'Label Reprint',
    manualWeight: 'Manual Weight',
    sessionComplete: 'Session Complete',
    wasteRecord: 'Waste Record',
    noOverrides: 'No overrides found for this date',
  },

  // Warehouse Transfer Management
  warehouseTransfer: {
    // Page
    title: 'Warehouse Transfer Management',
    subtitle: 'Create and manage warehouse transfers between Way Fong and HTE',
    createTransfer: 'Create Transfer',
    editTransfer: 'Edit Transfer',
    transferDetails: 'Transfer Details',
    transferList: 'Transfer List',
    transferNotFound: 'Transfer not found',

    // Direction
    direction: 'Direction',
    wfToHte: 'Way Fong → HTE',
    hteToWf: 'HTE → Way Fong',
    wfToHteShort: 'WF → HTE',
    hteToWfShort: 'HTE → WF',

    // Status
    status: 'Status',
    draft: 'Draft',
    pending: 'Pending',
    scanned: 'Scanned',
    complete: 'Complete',
    cancelled: 'Cancelled',

    // Form labels
    transferNumber: 'Transfer Number',
    transferDate: 'Transfer Date',
    requestDate: 'Request Date',
    warehouseFrom: 'Warehouse From',
    warehouseTo: 'Warehouse To',
    locationFrom: 'Location From',
    locationTo: 'Location To',
    locationUpdated: 'Destination location updated',
    locationUpdateError: 'Failed to update destination location',
    clearCheckpointError: 'Failed to clear previous scan progress — you may see a stale resume prompt',
    memo: 'Memo',
    requestBy: 'Requested By',
    receiveBy: 'Received By',
    createdBy: 'Created By',
    completedBy: 'Completed By',
    completedAt: 'Completed At',

    // Line items
    lineItems: 'Line Items',
    addItem: 'Add Item',
    itemNumber: 'Item No.',
    itemName: 'Item Name',
    itemSearch: 'Search items...',
    quantityRequest: 'Qty Req.',
    quantityShipped: 'Qty Shipped',
    weightShipped: 'Weight Shipped',
    useSubUnit: 'Use Sub Unit',
    isFrozen: 'Frozen',
    dateCode: 'Date Code',
    remark: 'Remark',
    unit: 'Unit',
    brand: 'Brand',
    description: 'Description',
    noItems: 'No line items added',

    // Weight info
    weights: 'Weights',
    addWeight: 'Add Weight',
    weight: 'Weight',
    weightLb: 'Weight (lb)',
    pieceWeights: 'Piece Weights',
    noWeights: 'No weights recorded',

    // Actions
    submit: 'Submit',
    markComplete: 'Mark Complete',
    cancelTransfer: 'Cancel Transfer',
    downloadPdf: 'Download PDF',
    print: 'Print',
    printPdf: 'Print PDF',
    receiveTransfer: 'Receive Transfer',
    receive: 'Receive',
    saveAsDraft: 'Save as Draft',
    confirmCancel: 'Are you sure you want to cancel this transfer?',
    confirmDelete: 'Delete this draft transfer?',
    deleteSuccess: 'Transfer deleted',
    confirmComplete: 'Mark this transfer as complete? This cannot be undone.',
    confirmSubmit: 'Submit this transfer? It will move from draft to pending.',
    confirmDeleteItem: 'Remove this item from the transfer?',

    // Summary cards
    totalTransfers: 'Total Transfers',
    drafts: 'Drafts',
    pendingTransfers: 'Pending',
    pendingTransferBacks: 'Pending Transfer Backs',
    completedTransfers: 'Completed',

    // Table
    lines: 'Items',
    created: 'Created',
    noTransfers: 'No transfers found',
    noTransfersDesc: 'Create a new warehouse transfer to get started.',

    // Filters
    allDirections: 'All Directions',
    dateRange: 'Date Range',
    searchPlaceholder: 'Search by transfer number or memo...',

    // PDF
    pdfTitle: 'WAREHOUSE TRANSFER',
    pdfTitleCn: '存貨倉庫轉移',
    pdfPage: 'Page',
    pdfDate: 'Date',
    pdfRequestDate: 'Request Date',
    pdfTransferNo: 'Transfer No.',
    pdfRequestedBy: 'Requested By',
    pdfReceivedBy: 'Received By',
    pdfFrom: 'FROM',
    pdfTo: 'TO',
    pdfSubtotal: 'Subtotal',
    pdfSignature: 'Signature',

    // Validation
    directionRequired: 'Please select a direction',
    warehouseFromRequired: 'Warehouse From is required',
    warehouseToRequired: 'Warehouse To is required',
    itemRequired: 'Please select an item',
    weightPositive: 'Weight must be a positive number',
    cannotEditNonDraft: 'Can only edit transfers in draft status',
    needLineItems: 'Transfer must have at least one line item',

    // Messages
    createSuccess: 'Transfer created successfully',
    updateSuccess: 'Transfer updated successfully',
    cancelSuccess: 'Transfer cancelled',
    completeSuccess: 'Transfer marked as complete',
    submitSuccess: 'Transfer submitted',
    itemAdded: 'Item added to transfer',
    itemUpdated: 'Item updated',
    itemRemoved: 'Item removed from transfer',
    weightAdded: 'Weight entry added',
    weightRemoved: 'Weight entry removed',

    // Report tab
    transfersTab: 'Transfers',
    transfersInTab: 'Transfers In',
    transferBacksTab: 'Transfer Backs / Outputs',
    reportTab: 'Report',
    reportTitle: 'Transfer Report',
    reportSubtitle: 'Detail-level report across completed transfers',
    dateFrom: 'Date From',
    dateTo: 'Date To',
    itemFrom: 'Item From',
    itemTo: 'Item To',
    brandFilter: 'Brand',
    warehouseFromFilter: 'Warehouse From',
    warehouseToFilter: 'Warehouse To',
    searchReport: 'Search',
    exportCsv: 'Export CSV',
    transferFrom: 'Transfer From',
    qty: 'Qty',
    totalItems: 'Total Items',
    totalQty: 'Total Qty',
    totalWeight: 'Total Weight',
    noReportData: 'No report data found. Adjust filters and search again.',
    reportDateRequired: 'Date range is required to search the report.',

    // ICS Transfers tab
    icsTransfersTab: 'VB6 Transfers',
    searchType: 'Search By',
    searchByWarehouse: 'Warehouse Code',
    searchByTransferNo: 'Transfer No.',
    searchByRequestDate: 'Request Date',
    searchByTransferDate: 'Transfer Date',
    searchByHte: 'HTE Transfers',
    searchByPending: 'Pending Requests',
    selectWarehouse: 'Select a warehouse...',
    selectLocation: 'Select a location...',
    searchIcs: 'Search',
    allLocations: 'All Locations',
    allStatuses: 'All',
    itemCodeFilter: 'Item Code',
    itemCodePlaceholder: 'Filter by item code...',
    transferNumberSearch: 'Transfer #',
    transferNumberPlaceholder: 'Search transfer #...',
    icsTransferDetails: 'Transfer Detail',
    itemCount: 'Items',
    noIcsResults: 'No transfers found',
    enterSearchValue: 'Enter search value',
    frozen: 'Frozen',
    backToResults: 'Back to Search',
    packing: 'Packing',
    weightsCount: 'Weights',
    itemsFound: 'Items Found',
    goToRecord: 'Go to Record',
    searchTitle: 'Search Inventory Warehouse Transfer Record',
    operator: 'Operator',
    dateTimeRequested: 'Date/Time Requested',
    totalWeightLabel: 'Total Weight',
    from: 'From',
    to: 'To',
    today: 'Today',
    week: '1 Week',
    month: 'Month',
    completeCheck: 'Complete',
    specialRequest: 'Special Request',
    newTransfer: 'New Transfer',
    saveAndSubmit: 'Save & Submit',
    savingTransfer: 'Saving...',
    backToList: 'Back to List',
    searchItemPlaceholder: 'Search by item number or name...',

    // Transfer detail form
    selectItemToEdit: 'Click an item row below to view / edit its details',
    additionalDetails: 'Additional Details',
    avgSold: 'Avg. Sold',
    avgBack: 'Avg. Back',
    qsType: 'QS Type',
    duration: 'Duration',
    scanComplete: 'Scan Complete',
    qsPend: 'QS Pend',
    qsRecNumber: 'QS Rec #',
    salespersonQs1: 'Salesperson QS1',
    qItem: 'Q-Item',
    findItem: 'Find...',
    searchItems: 'Search Items',
    cs: 'CS',
    pc: 'PC',
    noSearchResults: 'No items found',
  },

  // Production Planning
  planning: {
    title: 'Weekly Production Planning',
    planMode: 'Plan Mode',
    viewMode: 'View Mode',
    addRow: 'Add Row',
    savePlan: 'Save Plan',
    date: 'Date',
    productSku: 'Product SKU',
    productName: 'Product Name',
    packing: 'Packing',
    cases: 'Cases',
    estProductionLbs: 'Est. Production lbs',
    material: 'Material',
    materialName: 'Material Name',
    supplierBrand: 'Supplier/Brand',
    location: 'Location',
    fulfillment: 'Fulfillment',
    stockWarning: 'Stock Warning',
    yieldFactor: 'Yield Factor',
    required: 'Required',
    available: 'Available',
    shortfall: 'Shortfall',
    transferPending: 'Transfer Pending',
    fulfilled: 'Fulfilled',
    partial: 'Partial',
    unfulfilled: 'Unfulfilled',
    pending: 'Pending',
    notRequired: 'N/A',
    noProductSelected: 'Select a product',
    sessionCreated: 'Plan session created',
    sessionsFailed: 'Failed to create plan session',
    removeRow: 'Remove row',
    selectProduct: 'Select product...',
    autoResolved: 'Auto-resolved from product inputs',
    planOnlySession: 'Plan-only (no material allocation)',
    materialFulfillment: 'Material Status',
    listView: 'List View',
    calendarView: 'Calendar',
    oneDay: 'Day',
    oneWeekShort: 'Week',
    oneWeek: '1 Week',
    twoWeek: '2 Week',
    month: 'Month',
    today: 'Today',
    more: 'more',
    legend: 'Legend',
    statusPending: 'Pending',
    statusSubmitted: 'Submitted',
    statusReceived: 'Received',
    statusPlanned: 'Submitted', // legacy alias (WCO-2146)
    statusReady: 'Ready',
    statusActive: 'Active',
    statusPaused: 'Paused',
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
    finishedProduct: 'Finished Product',
    rawMaterial: 'Raw Material',
    viewFullDetails: 'View Full Details',
    productInfo: 'Product Information',
    materialInfo: 'Material Information',
  },

  // Daily Transfer Out
  dailyTransferOut: {
    title: 'Daily Transfer Out',
    finishedProducts: 'Finished Products',
    finishedProductsNotIntact: 'Finished Products not intact',
    finishedNotIntactBadge: 'Not Intact',
    // WCO-2623: supervisor Reprint override
    reprint: 'Reprint',
    reprintOverrideTooltip: 'Reprint this label — supersedes the current one',
    reprintDialogTitle: 'Reprint label',
    reprintDialogBody: 'Reprint the label for {{item}}? This supersedes the current label — the old one will no longer be valid.',
    reprintReasonLabel: 'Reason',
    reprintReasonPlaceholder: 'e.g. label misplaced',
    reprintConfirm: 'Reprint & supersede',
    reprinting: 'Reprinting…',
    reprintSuccess: 'Label reprinted',
    reprintFailed: 'Failed to reprint label',
    niLabelsAlreadyPrinted: 'Labels already printed',
    niLabelsPrinted: 'Printed {{count}} label(s)',
    niLabelsPrintFailed: 'Failed to print labels',
    rejectedMaterials: 'Rejected Materials',
    remainingMaterials: 'Available Remaining Raw Material',
    remainderProducedToday: "Today's Remainder",
    expectedQty: 'Exp. Qty',
    expectedWeight: 'Exp. Weight',
    actualQty: 'Act. Qty',
    actualWeight: 'Act. Wgt',
    variance: 'YF Variance',
    source: 'Source',
    refreshActuals: 'Refresh Actuals',
    finalizeTransferOut: 'Finalize Transfer Out',
    submitTransfer: 'Submit Transfer',
    confirmSubmit: 'This will create the ICS transfer document and make it visible to the warehouse. Continue?',
    submitSuccess: 'Transfer submitted successfully',
    submitFailed: 'Failed to submit transfer',
    outputDestinations: 'Output Destinations',
    destination: 'Destination',
    expectedCases: 'Expected',
    actualCases: 'Actual',
    casesVariance: 'Variance',
    tombstoned: 'Cancelled',
    unscannedBoxesWarning: 'box(es) not yet scanned to a destination — scan them before submitting',
    allBoxesScanned: 'All produced boxes scanned to a destination',
    outputLabel: 'Output',
    transferLabel: 'Transfer',
    noTransferForDay: 'No transfer document for this day yet.',
    transferNotSubmittedYet: 'This transfer has not been submitted yet — scan cases into it, then submit to generate its document.',
    selectTransfer: 'Transfer:',
    noOutputForDay: 'No production output for this day yet.',
    outputNoRecordYet: 'This output has no production record yet — it is created when the output is sent.',
    selectOutput: 'Output:',
    newTransferBack: 'New Transfer-Back',
    newLabel: 'New',
    typeLabel: 'Type',
    selectDestination: 'Select destination',
    create: 'Create',
    transferCreated: 'Transfer created',
    outputCreated: 'Output created',
    createFailed: 'Failed to create',
    rawMaterialTransfer: 'Transfer',
    newRawMaterialTransfer: 'New Raw-Material Transfer',
    noRawMaterialsToTransfer: 'No raw materials to transfer',
    createTransfer: 'Create Transfer',
    rawTransferCreated: 'Raw-material transfer created',
    rawTransferFailed: 'Failed to create raw-material transfer',
    incompleteSessionsWarning: 'Cannot finalize — some production sessions are not yet completed',
    status: 'Status',
    type: 'Type',
    prePopulated: 'Pre-populated',
    actualsWithinTolerance: 'Within Tolerance',
    varianceWarning: 'Variance Warning',
    finalized: 'Shipped',
    notShipped: 'Not Shipped',
    complete: 'Complete',
    noDataForDate: 'No transfer out data for this date',
    confirmFinalize: 'Are you sure you want to finalize this daily transfer out?',
    finalizeSuccess: 'Daily transfer out finalized successfully',
    pdfDownloadFailed: 'Failed to download transfer PDF',
    downloadTransferPdf: 'Download Transfer PDF',
    finalizeFailed: 'Failed to finalize daily transfer out',
    refreshSuccess: 'Actuals refreshed successfully',
    refreshFailed: 'Failed to refresh actuals',
    noItemsToRefresh: 'No items to refresh — complete a session first',
    autoGenerated: 'Auto-generated',
    manual: 'Manual',
    dailyOut: 'Transfer Back',
    sourceFilter: 'Source Filter',
    linkedSessions: 'Linked Sessions',
    itemNumber: 'Item No.',
    itemName: 'Item Name',
    category: 'Category',
    notes: 'Notes',
    expectedYF: 'Exp. YF',
    actualYF: 'Act. YF',
    estimate: 'Estimate',
    completed: 'Completed',
    rejected: 'Rejected',
    remainder: 'Remainder',
    byProduct: 'By-Product',
    byProducts: 'By Products',
    byProductMarketable: 'Marketable',
    byProductProduction: 'For Production',
    packing: 'Packing',
    tabSummary: 'Production Summary',
    tabTransferDetail: 'Transfer Detail',
    tabOutputDetail: 'Output Detail',
    transfer: 'Transfer',
    excludeFailed: 'Failed to update exclusion',
    reserve: 'Reserve',
    casesRow: 'Cases',
    bufferRow: 'Buffer',
  },

  // Box Search
  boxSearch: {
    title: 'Box Search',
    subtitle: 'Search and filter finished product boxes',
    filters: 'Filters',
    item: 'Item',
    selectItem: 'Select item...',
    allItems: 'All Items',
    dateCode: 'Date Code',
    dateCodePlaceholder: 'e.g., 26037',
    packedFrom: 'Packed From',
    packedTo: 'Packed To',
    location: 'Location',
    selectLocation: 'Select location...',
    allLocations: 'All Locations',
    includeVoided: 'Include Voided',
    search: 'Search',
    exportExcel: 'Export Excel',
    print: 'Print',
    summary: 'Summary',
    totalBoxes: 'Total Boxes',
    totalWeight: 'Total Weight',
    boxNumber: 'Box Number',
    itemNumber: 'Item No.',
    productName: 'Product Name',
    weight: 'Weight',
    packedAt: 'Packed At',
    operator: 'Operator',
    packer: 'Packer',
    packedBy: 'Packed By',
    status: 'Status',
    session: 'Session',
    station: 'Station',
    noResults: 'No boxes found matching the criteria',
    lbs: 'lbs',
    barcode: 'Barcode',
    clearFilters: 'Clear',
  },

  // Yield Variance Report
  yieldVariance: {
    title: 'Yield Variance Report',
    description: 'Sessions with ±1% or more yield variance from expected',
    fetchError: 'Failed to load yield variance data',
    totalVariances: 'Total Variances',
    overYield: 'Over Yield',
    underYield: 'Under Yield',
    avgVariance: 'Avg Variance',
    startDate: 'Start Date',
    endDate: 'End Date',
    product: 'Product',
    operator: 'Operator',
    station: 'Station',
    varianceType: 'Variance Type',
    allVariances: 'All Variances',
    overYieldOnly: 'Over Yield Only',
    underYieldOnly: 'Under Yield Only',
    session: 'Session',
    date: 'Date',
    expectedYield: 'Expected',
    actualYield: 'Actual',
    variance: 'Variance',
    lfExpected: 'LF Expected',
    lfActual: 'LF Actual',
    lfVariance: 'LF Variance',
    needsReview: 'Needs review',
    anomalyTooltip: 'Actual labour factor deviates ±20% from standard',
    observedThroughput: 'Throughput (lb/wh)',
    productiveTime: 'Productive Time',
    workers: 'Workers',
    throughputCalc: {
      title: "Throughput (lb/wh) — how it's calculated",
      output: 'Output',
      productiveTime: 'Productive time',
      hrs: 'hrs',
      workers: 'Workers',
      step1: 'Step 1 — per-worker rate = output ÷ hrs ÷ workers',
      perWorkerHour: 'lb per worker-hour',
      step2: 'Step 2 — × standard crew size (2)',
      why: '÷workers gives an honest per-person rate; ×2 puts it on the standard 2-worker basis so a crew at standard scores LF ≈ 1.0 at any headcount.',
      notMeasurable: 'Not measurable — missing output, worker count, or productive time for this run.',
      roundingNote: 'Values are rounded for display; the result is computed at full precision, so re-multiplying the shown 2-decimal numbers by hand may differ by ~0.01.',
    },
    approvedBy: 'Approved By',
    noData: 'No yield variances found for the selected filters',
    weightDetails: 'Weight Details',
    throughputLabour: 'Throughput & Labour',
    operatorStation: 'Operator / Station',
    expandRowDetails: 'Show row details for',
    collapseRowDetails: 'Hide row details for',
    inputWeight: 'Input Weight',
    targetWeight: 'Target Weight',
    outputWeight: 'Output Weight',
    carryover: 'Carryover',
    rawMaterial: 'Raw Material',
    wasteBreakdown: 'Waste Breakdown',
    noWasteData: 'No waste data recorded',
    approvalDetails: 'Approval Details',
    approvedAt: 'Approved At',
    reason: 'Reason',
    overrideNoDetails: 'Override completion (no approval details)',
    noApprovalRequired: 'No approval required',
    // Recurring variance suggestions
    suggestionsTitle: 'Recurring Yield Variances',
    suggestionsDescription: 'material(s) have recurring yield variances — review suggested yield factor updates',
    suggestionsLoading: 'Checking for recurring variances...',
    suggestionsError: 'Failed to load suggestions',
    finishedProduct: 'Finished Product',
    currentYield: 'Current Yield',
    suggestedYield: 'Suggested Yield',
    occurrences: 'Occurrences',
    lastOccurrence: 'Last Occurrence',
    recentHistory: 'Recent Variance History',
    yieldSource: 'Source',
    hideSuggestions: 'Hide',
    showSuggestions: 'Review',
    wasteTypes: {
      byProduct: 'By-Product',
      byProductMarketable: 'By-Product (Marketable)',
      disposable: 'Disposable Waste',
      waterLiquid: 'Water / Liquid Loss',
      humanError: 'Human Error',
      remainderIntact: 'Remainder (Intact)',
      remainderNotIntact: 'Remainder (Not Intact)',
      finishProductNotIntact: 'Finish Product (Not Intact)',
    },
  },

  // Work Schedule
  workSchedule: {
    title: 'Work Schedule',
    weeklySchedule: 'Weekly Work Schedule',
    employee: 'Employee',
    operators: 'Operators',
    packers: 'Packers',
    stations: 'Stations',
    activeStations: 'Active Stations',
    off: 'Off',
    cutRole: 'Operator (CUT)',
    packRole: 'Packer (PACK)',
    stationActive: 'Station Active',
    copyToNextWeek: 'Copy to Next Week',
    unsavedChanges: 'You have unsaved changes',
    loadError: 'Failed to load schedule',
    saveSuccess: 'Schedule saved successfully',
    saveError: 'Failed to save schedule',
    copySuccess: 'Copied {{count}} assignments to next week',
    copyError: 'Failed to copy schedule',
    summary: 'Summary',
  },

  // Approval History Report
  approvalHistory: {
    title: 'Approval History',
    subtitle: 'Audit trail of manager approvals and rejections for on-hold inventory items',
    dateRange: 'Date Range',
    dateFrom: 'From',
    dateTo: 'To',
    actionFilter: 'Action',
    searchPlaceholder: 'Search by item # or name...',
    date: 'Date',
    itemNumber: 'Item #',
    itemName: 'Item Name',
    transferNumber: 'Transfer #',
    quantity: 'Qty',
    weight: 'Weight',
    holdReason: 'Hold Reason',
    action: 'Action',
    manager: 'Manager',
    receivedBy: 'Received By',
    receivedAt: 'Received At',
    notes: 'Notes',
    approved: 'Approved',
    rejected: 'Rejected',
    all: 'All',
    noRecords: 'No approval history found',
    noRecordsDesc: 'There are no approval or rejection records matching your filters.',
    holdReasons: {
      temperature: 'Temperature',
      damaged: 'Damaged',
      wrong_quantity: 'Wrong Quantity',
      quality: 'Quality',
      expiration: 'Expiration',
      other: 'Other',
    },
  },

  // Item Summary Report
  itemSummaryReport: {
    title: 'Item Summary Report',
    rawMaterials: 'Raw Materials',
    finishedProducts: 'Finished Products',
    itemNumber: 'Item #',
    itemName: 'Name',
    brand: 'Brand',
    location: 'Location',
    yieldFactor: 'Yield Factor',
    laborFactor: 'Labor Factor',
    lastUsed: 'Last Used',
    lastProduced: 'Last Produced',
    materials: 'Materials',
    search: 'Search by item # or name...',
    allBrands: 'All Brands',
    allLocations: 'All Locations',
    startDate: 'Start Date',
    endDate: 'End Date',
    noData: 'No items found',
    noDataHint: 'Try adjusting your filters',
    laborPlaceholder: 'Coming soon',
    source: 'Source',
    expandMaterials: 'Show materials',
    collapseMaterials: 'Hide materials',
    page: 'Page',
    of: 'of',
    showing: 'Showing',
    items: 'items',
  },

  // Clock In Modal (from Daily Queue row click)
  clockInModal: {
    title: 'Clock In to Session',
    subSessionInfo: 'Sub-Session Info',
    scanBadge: 'Scan Employee Badge',
    scanBadgeHint: 'Scan badge or enter employee number manually',
    enterManually: 'Or enter manually:',
    badgePlaceholder: 'Employee number...',
    lookingUp: 'Looking up employee...',
    selectRole: 'Select Role',
    operator: 'Operator',
    packer: 'Packer',
    clockIn: 'Clock In',
    clockingIn: 'Clocking in...',
    currentAssignments: 'Current Assignments',
    notAssigned: 'Not assigned',
    since: 'since',
    noStation: 'No station assigned to this sub-session',
    sessionNotActive: 'This session cannot accept clock-ins (status: {status})',
    employeeFound: 'Employee found',
    relieveConfirm: 'Relieve {name}?',
    relieveDescription: '{name} is currently working as {role}. They will be clocked out.',
    yesRelieve: 'Yes, Relieve & Clock In',
    alreadyHere: 'Already clocked in at this session',
    transferConfirm: 'Transfer from {station}?',
    transferDescription: 'Currently clocked in at {station} as {role}. Transfer here?',
    yesTransfer: 'Yes, Transfer',
    alreadyHerePrompt: 'What would you like to do?',
    scanOut: 'Scan Out',
    takeBreak: 'Take Break',
    scanOutSuccess: 'Scanned out successfully',
    breakStarted: 'Break started',
    currentlyAs: 'Currently scanned in as',
    onBreak: 'On break',
    success: 'Scanned in successfully',
    error: 'Failed to clock in',
    errorSessionPlanned: 'Session is not ready — assign an operator and packer first',
    errorSessionCompleted: 'This session has already been completed',
    errorSessionCancelled: 'This session has been cancelled',
    errorNoActiveSession: 'No active session at this station',
    materialsNotReceived: 'Raw materials for sub-session {subSessionNumber} have not been received yet. Production can\'t start.',
    awaitingAssignment: 'Transfer for sub-session {subSessionNumber} has been received, but the sub-session is waiting for Operator/Packer assignment.',
    scheduledFuture: 'Sub-session {subSessionNumber} is scheduled for a future date and cannot be started yet.',
  },

  // By-Product to Raw Material Mapping
  byproductMapping: {
    title: 'By-Product Mapping',
    subtitle: 'Link by-product items to their source raw materials',
    selectRawMaterial: 'Select a raw material',
    searchRawMaterials: 'Search raw materials...',
    linkedByproducts: 'Linked By-Products',
    noByproductsLinked: 'No by-products linked yet',
    addByproduct: 'Add By-Product',
    removeByproduct: 'Remove by-product',
    confirmRemove: 'Remove?',
    mappingCreated: 'By-product linked successfully',
    mappingDeleted: 'By-product unlinked successfully',
    searchItems: 'Search items...',
    notes: 'Notes',
    noResults: 'No results found',
    alreadyLinked: 'All matching items are already linked',
    existingLinkages: 'Existing Linkages',
    byproductCount: '{{count}} by-product(s)',
    noMappingsYet: 'No linkages created yet',
    productMappingTitle: 'Product Mapping',
    byproductTab: 'By-Product Mapping',
    inputOutputTab: 'Input / Output Mapping',
    packingMaterialTab: 'Packing Material Mapping',
    comingSoon: 'Coming Soon',
    comingSoonDescription: 'This feature is under development and will be available soon.',
    inputOutputDescription: 'Define which raw materials are used to produce each finished product. This mapping will be used for production planning and material requirements.',
    packingMaterialDescription: 'Link packing materials (boxes, bags, labels) to their associated products. This mapping will be used for packaging requirements and inventory planning.',
  },

  // Input / Output Mapping
  inputOutputMapping: {
    subtitle: 'View which raw materials are used to produce each HTE finished product (from ICS)',
    selectProduct: 'Select a product',
    searchProducts: 'Search products...',
    noResults: 'No results found',
    selectProductPrompt: 'Select a product from the list to view its raw materials',
    rawMaterials: 'Raw Materials',
    noRawMaterials: 'No raw materials found for this product',
    itemNumber: 'Item #',
    name: 'Name',
    yieldFactor: 'Yield',
    laborFactor: 'Labor',
    batchSize: 'Batch Size',
    teamSize: 'Team Size',
    comments: 'Comments',
    addRawMaterial: 'Add Raw Material',
    searchRawMaterials: 'Search raw materials...',
    preference: 'Preference #',
    alreadyLinked: 'Already linked',
    mappingCreated: 'Raw material linked successfully',
  },

  // Labour Factor management (standard-based model, WCO-2474)
  labourFactor: {
    tabTitle: 'Labor Factor',
    subtitle: 'Set the standard OUTPUT throughput (lb/hr for a 2-worker crew, yield already included) for each raw material of a product. The Labor Factor is derived automatically.',
    selectProduct: 'Select a product',
    searchProducts: 'Search products...',
    selectProductPrompt: 'Select a product from the list to set its labor factors',
    noResults: 'No results found',
    materials: 'Raw Materials',
    noMaterials: 'No raw materials found for this product',
    itemNumber: 'Item #',
    name: 'Name',
    throughput: 'lb/hr (2-crew)',
    allowance: 'Allowance %',
    labourFactor: 'Labor Factor',
    baseline: 'Baseline',
    noBaselineHint: 'No baseline selected yet — pick one input as the baseline to derive labor factors.',
    baselineNeedsThroughput: 'Set this input\'s lb/hr before making it the baseline.',
    invalidThroughput: 'Enter a positive number, or clear the field to unset.',
    saved: 'Throughput saved',
    allowanceSaved: 'Allowance saved',
    invalidAllowance: 'Enter a percentage between 0 and 100.',
    baselineSet: 'Baseline updated',
    saveFailed: 'Failed to save',
    history: 'History',
    historyTitle: 'Change history',
    historyEmpty: 'No changes recorded yet',
    historyLoading: 'Loading…',
    historyThroughput: 'lb/hr',
    historyBaselineSet: 'Set as baseline',
    historyBaselineUnset: 'Removed as baseline',
    close: 'Close',
  },

  // Packing Material Mapping
  packingMaterialMapping: {
    title: 'Packing Material Mapping',
    subtitle: 'Link packing materials (boxes, labels, bags) to HTE finished products',
    defaultAllProducts: 'Default (All Products)',
    selectProduct: 'Select a product',
    searchProducts: 'Search products...',
    linkedPackingMaterials: 'Linked Packing Materials',
    noPackingMaterialsLinked: 'No packing materials linked yet',
    addPackingMaterial: 'Add Packing Material',
    removePackingMaterial: 'Remove packing material',
    confirmRemove: 'Remove?',
    mappingCreated: 'Packing material linked successfully',
    mappingDeleted: 'Packing material unlinked successfully',
    searchItems: 'Search packing materials...',
    notes: 'Notes',
    noResults: 'No results found',
    alreadyLinked: 'All matching items are already linked',
    existingLinkages: 'Existing Linkages',
    packingMaterialCount: '{{count}} packing material(s)',
    noMappingsYet: 'No linkages created yet',
    level_case: 'Per Case',
    level_subunit: 'Per Sub-unit',
    qtyPerBox: 'Qty per box',
    packingMaterials: 'Packing Materials',
    qtyNeeded: 'Needed',
    qtyToTransfer: 'Transfer',
    available: 'Available',
    stockSufficient: 'Stock sufficient',
    stockInsufficient: 'Insufficient stock',
  },

  // User Management (Supervisors managing operators)
  userManagement: {
    title: 'User Management',
    subtitle: 'Manage operator accounts',
    addOperator: 'Add Operator',
    selectEmployee: 'Select Employee',
    searchEmployees: 'Search employees...',
    searchOperators: 'Search operators...',
    noEmployeesAvailable: 'No employees available to add',
    noOperators: 'No operators found',
    userAdded: 'Operator added successfully',
    userUpdated: 'User updated successfully',
    generatedPassword: 'Password',
    userDeleted: 'Operator deleted successfully',
    employeeNumber: 'Employee #',
    username: 'Username',
    fullName: 'Full Name',
    lastModified: 'Last Modified',
    createdAt: 'Created',
    name: 'Name',
    email: 'Email',
    active: 'Active',
    inactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    confirmDelete: 'Delete User',
    deleteWarning: 'Are you sure you want to delete {{name}}? This action cannot be undone.',
    defaultPasswordNote: 'The default password will be the employee number. The operator should change it on first login.',
    userId: 'User ID',
    userIdPlaceholder: 'Enter user ID...',
    userIdTooShort: 'User ID must be at least 3 characters',
    userIdTaken: 'User ID is already taken',
    passwordFromUserId: 'The default password will be the User ID + a random 4-digit number. The operator should change it on first login.',
    userDetails: 'User Details',
    editUser: 'Edit User',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    enterNewPassword: 'Enter new password...',
    enterNewPasswordHint: 'Enter a new password and click the edit button to save.',
    confirmPasswordChange: 'Change Password',
    confirmPasswordChangeMessage: 'Are you sure you want to change this user\'s password?',
    newPassword: 'New password',
    passwordSaved: 'Password saved successfully',
    passwordEncrypted: 'Password is encrypted',
  },

  icsUserManagement: {
    title: 'ICS User Access',
    subtitle: 'Enable ICS employees to access the production system',
    searchPlaceholder: 'Search by name, user ID, or employee #...',
    noResults: 'No users found',
    userId: 'User ID',
    name: 'Name',
    employeeNum: 'Employee #',
    icsRole: 'ICS Role',
    prodRole: 'Production Role',
    active: 'Active',
    disabled: 'Disabled',
    notEnabled: 'Not Enabled',
    icsDisabled: 'ICS Disabled',
    pendingPassword: 'Pending Password',
    enable: 'Enable',
    disable: 'Disable',
    reEnable: 'Re-enable',
    enableUser: 'Enable User',
    userCreated: 'User Account Created',
    userEnabled: 'User enabled successfully',
    statusUpdated: 'Status updated',
    assignRole: 'Assign Production Role',
    tempPassword: 'Temporary Password',
    tempPasswordInfo: 'Share this temporary password with the user. They will be required to set a new password on first login.',
    tempPasswordNote: 'This password will only be shown once. Make sure to write it down or share it with the user.',
    addUser: 'Add User',
    allEnabled: 'All ICS employees have been enabled',
    deleteUser: 'Delete User',
    deleteWarning: 'Are you sure you want to permanently delete the production account for {{name}}? This will remove their login credentials and cannot be undone.',
    userDeleted: 'User account deleted',
    changeRole: 'Click to change role',
    roleUpdated: 'Role updated successfully',
    resetPassword: 'Reset Password',
    resetPasswordConfirm: 'This will reset the password to a temporary one. The user will need to change it on their next login.',
    passwordResetDone: 'Password Reset Complete',
    passwordReset: 'Password reset successfully',
    lastModified: 'Last Modified',
  },

  changePassword: {
    title: 'Set Your Password',
    subtitle: 'You must set a new password before continuing.',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'Enter your new password',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Enter the password again',
    requirements: 'Password Requirements:',
    minLength: 'At least 8 characters',
    uppercase: 'At least one uppercase letter',
    lowercase: 'At least one lowercase letter',
    digit: 'At least one number',
    special: 'At least one special character (!@#$%^&*)',
    passwordsMatch: 'Passwords match',
    submit: 'Set Password',
    success: 'Password set successfully. Please log in again.',
  },

  // Camera Barcode Scanner
  cameraScanner: {
    title: 'Scan Barcode',
    instruction: 'Point the camera at a barcode',
    permissionDenied: 'Camera permission denied. Please allow camera access in your browser settings.',
    noCamera: 'No camera found on this device.',
    error: 'Failed to start camera.',
    openCamera: 'Use Camera',
  },

  // WCO-2322 (6B): per-output production scan modal
  productionOutputScan: {
    title: 'Scan Production Outputs',
    selectOutput: 'Select Output',
    scanCases: 'Scan Cases',
    selectPrompt: 'Choose a transfer',
    outputRow: 'Output',
    transferRow: 'Transfer',
    cases: 'cases',
    noRows: 'No outputs or transfers for this day',
    switch: 'Switch',
    scanPrompt: 'Scan a case...',
    noScans: 'No scans yet',
    extrasHint: 'FP-NI / by-products (extra — not counted toward expected cases)',
    fpniTag: 'FP-NI',
    byproductTag: 'By-product',
    send: 'Send',
    sent: 'Sent',
    sentBadge: 'Sent',
    sentHint: 'Already sent to the warehouse',
    confirmStep: 'Confirm',
    confirmPrompt: 'Review every output and transfer before sending. All must be fully scanned.',
    allSentComplete: 'All outputs and transfers for this day have already been sent — nothing left to scan.',
    complete: 'Complete',
    incomplete: 'Incomplete',
    reviewConfirm: 'Confirm',
    reviewStep: 'Review',
    backToScan: 'Back',
    confirmSend: 'Confirm & Send',
    sendAllReady: 'Send all ready',
    noneReady: 'No destination is fully scanned yet',
    confirmSendAll: 'Send {{count}} ready container(s)?',
    incompleteBlocked: 'Not all outputs/transfers are fully scanned',
    confirmComingSoon: 'Review complete — the send step is wired in the next phase',
    confirmPopupBody: 'This writes the production, sales, and warehouse-transfer records and ships everything to the warehouse. Continue?',
    delete: 'Delete',
    deleted: 'Deleted',
    confirmDelete: 'Delete this draft? Its number will be reused.',
    // WCO-2412: per-session reconciliation (grouped scan view + target tooltip)
    plannedSurplus: '{{planned}} planned, +{{delta}} surplus',
    plannedDeficit: '{{planned}} planned, −{{delta}} deficit',
    extrasGroup: 'Extras (FP-NI / by-products)',
    targetBreakdown: 'Actual breakdown',
    targetTotal: 'Actual total',
    colPlan: 'Expected',
    colTarget: 'Actual',
  },

  // Ship-Out Scan Log
  shipOutScan: {
    title: 'Scan Transfer Items',
    scanTransfer: 'Scan Transfer Barcode',
    enterTransferNumber: 'Enter or scan transfer number...',
    orSelectTransfer: 'Or select a pending transfer',
    scanBadge: 'Scan Your Badge',
    enterBadge: 'Scan your employee badge...',
    scanBoxes: 'Scan Boxes',
    scanBoxBarcode: 'Scan box barcode...',
    totalScanned: 'Total Scanned',
    duplicateBox: 'This box has already been scanned for this transfer',
    boxScanned: 'Box scanned',
    scanFailed: 'Failed to record scan',
    // WCO-2545 — manual "Add damaged case" (unscannable intact label)
    addDamagedCase: 'Add damaged case (no barcode)',
    damagedCasesAvailable: 'Damaged cases (click to add)',
    addToTransfer: 'Add to transfer',
    manualDamagedItem: 'Item',
    manualDamagedItemPlaceholder: 'Select item…',
    manualDamagedWeight: 'Weight',
    manualDamagedItemRequired: 'Select the item for the damaged case',
    manualDamagedWeightRequired: 'Enter the weight of the damaged case',
    manualDamagedAdded: 'Damaged case added to transfer',
    // WCO-2465 — targeted scan-error messages (mapped from backend error codes)
    errWrongTargetOutput_finished: 'This is a finished-product box — scan it into the production output, not the raw-material transfer.',
    errWrongTargetOutput_byproduct: 'This is a by-product case — scan it into the production output, not the raw-material transfer.',
    errWrongTargetOutput_fpni: 'This is an FP-NI item — scan it into the production output, not the raw-material transfer.',
    errWrongTargetOutput: 'This item belongs to a production output — scan it there, not the raw-material transfer.',
    errWrongTargetTransfer: 'This is a raw-material label — scan it into its warehouse transfer, not the production output.',
    errWrongDay: "This material belongs to a different day's transfer ({{otherDate}}), not this one.",
    errUnrecognized: "Unrecognized barcode — it doesn't match anything for this scan.",
    undoScan: 'Undo',
    transferNotFound: 'Transfer not found',
    invalidBadge: 'Invalid badge',
    employeeNotFound: 'Employee not found',
    scanRemoved: 'Scan removed',
    clearAll: 'Clear All',
    clearAllConfirm: 'Are you sure you want to clear all scans for this transfer?',
    clearAllSuccess: 'All scans cleared',
    noScansYet: 'No boxes scanned yet',
    loadingTransfer: 'Loading transfer...',
    scannedBy: 'Scanned by',
    step: 'Step',
    barcodeNotFound: 'Barcode not found in production inventory',
    itemNotInTransfer: 'This item is not in the transfer',
    sessionsNotCompleted: 'Production sessions must be completed before scanning',
    confirmScan: 'Confirm Scan',
    scanConfirmed: 'Ship-out scan confirmed',
    remainingMaterial: 'Remaining Material',
    loggedFromWaste: 'Logged from waste tracking',
    summaryTitle: 'Expected vs Scanned',
    item: 'Item',
    expectedQty: 'Exp Qty',
    scannedQty: 'Scanned',
    expectedWt: 'Exp Wt',
    scannedWt: 'Scan Wt',
    remainingIntact: 'Remaining (Intact)',
    remainingNotIntact: 'Remaining (Not Intact)',
    unusedLot: 'Unused Lot',
    confirmOnPalette: 'Confirm on Palette',
    manualConfirm: 'Manual Confirm',
    byProduct: 'By-Product',
    byProductMarketable: 'By-Product (Marketable)',
    byProductProduction: 'By-Product (Production)',
    rejectedMaterial: 'Rejected Material',
  },

  // Operator Efficiency
  operatorEfficiency: {
    sortBy: 'Sort by',
    yield: 'Yield',
    speed: 'Speed',
    both: 'Both',
    noHistory: 'No history',
    avgYield: 'Avg Yield',
    avgSpeed: 'Avg Speed',
    lbsPerHour: 'lbs/hr',
    sessions: 'sessions',
    rank: 'Rank',
  },

  // Dev Tools
  devTools: {
    title: 'Dev Tools',
    resetHteData: 'Reset HTE Data',
    resetDescription: 'Delete all HTE-generated data across Edge and the Cloud DB. Production tables are fully cleared. ICS records >= 900001 are removed. Sequences are reset. This cannot be undone.',
    resetConfirmTitle: 'Confirm HTE Data Reset',
    resetConfirmWarning: 'This will permanently delete ALL HTE production data from Edge, and HTE-generated ICS records (>= 900001) from Edge and the Cloud DB. Sequences will be reset to 900001.',
    typeToConfirm: 'Type RESET to confirm',
    resetButton: 'Reset All Data',
    resetting: 'Resetting...',
    resetSuccess: 'HTE data reset successful',
    resetPartial: 'HTE data reset completed with errors',
    resetError: 'Failed to reset HTE data',
    resetSummary: 'Deleted row counts by table:',
    rowsDeleted: 'rows',
    sequencesReset: 'Sequences reset',
    stationsReset: 'Stations reset to idle',
    dangerZone: 'Danger Zone',
    dangerZoneDescription: 'Irreversible actions for development and testing only.',
    edgeLocal: 'Edge Local DB',
    stagingVm: 'Cloud DB',
    gcpProd: 'GCP Prod (10.142.0.5)',
    oracle: 'Oracle',
    noRecords: 'No HTE records found',
    targetLabel: 'Target databases:',
    cloudDbTarget: 'Cloud DB target:',
    hostVerifyLoading: 'Verifying cloud DB target host...',
    hostVerifyError: 'Could not verify the cloud DB target host. Reset is blocked until the target can be confirmed.',
    targetAll: 'All (Edge + Cloud)',
    targetEdge: 'Edge Only',
    targetCloud: 'All Cloud (GCP + Staging VM + Oracle)',
    targetGcpProd: 'GCP Prod Only',
    targetStagingVm: 'Cloud (dev/staging)',
    targetOracle: 'Oracle Only',
  },
  dataRepair: {
    title: 'Data Repair',
    description: 'Preview, apply, and roll back targeted database repairs with guard validation.',
    prodAllowedNote: 'Data repairs are authorized for production use by admins. Guards are re-checked at apply time.',
    actionLabel: 'Repair action',
    selectAction: 'Select a repair action…',
    noActions: 'No repair actions are registered yet.',
    actionsLoading: 'Loading repair actions…',
    actionsLoadError: 'Could not load repair actions. Check your permissions or try again.',
    rowsTruncated: 'Showing {{shown}} of {{total}} rows.',
    inputChangedRepreview: 'Input changed — run Preview again before applying.',
    applySuccessWithId: 'Repair applied successfully. Repair log ID: {{id}}',
    applyResultTitle: 'Apply result (repair log ID {{id}})',
    reverseSyncStamped: 'Reverse sync: {{count}} row(s) stamped for propagation.',
    inputJson: 'Repair input (JSON)',
    inputJsonHint: '{} for default scope, or action-specific parameters',
    inputJsonInvalid: 'Repair input must be valid JSON object',
    acceptedParams: 'This repair accepts:',
    historyTitle: 'Recent repairs',
    historyId: 'Log #',
    historyAction: 'Repair',
    historyOperation: 'Operation',
    historyActor: 'By',
    historyWhen: 'When',
    historyOutcome: 'Outcome',
    historyRows: '{{n}} row(s) changed',
    historyRolledBack: 'Rolled back',
    historyFailed: 'Failed',
    notSimulated: 'Not simulated — the safety checks failed, so the repair was never run.',
    paramExample: 'example',
    insertExample: 'Insert example',
    noParams: 'This repair takes no parameters — leave {} as it is.',
    previewButton: 'Preview (dry run)',
    previewLoading: 'Previewing…',
    previewNote: 'Preview is a dry run — no changes are committed.',
    previewError: 'Preview failed',
    beforeState: 'Before',
    afterState: 'After',
    noRows: 'No matching rows',
    guardsPassed: 'All guards passed',
    guardsFailed: 'One or more guards failed',
    guardOk: 'OK',
    guardFailed: 'Failed',
    applyButton: 'Apply repair',
    applyLoading: 'Applying…',
    applySuccess: 'Repair applied successfully',
    applyError: 'Apply failed',
    applyConfirmTitle: 'Confirm repair apply',
    applyConfirmBody: 'This will mutate production data. The repair can be rolled back afterward if needed.',
    applyRecheckNote: 'Guards will be re-evaluated against the current database state before applying.',
    applyConfirmButton: 'Apply repair',
    rollbackButton: 'Rollback last apply',
    rollbackLoading: 'Rolling back…',
    rollbackSuccess: 'Rollback completed successfully',
    rollbackError: 'Rollback failed',
    reverseSyncNote: 'This action reverse-syncs repaired ICS rows to Oracle via the sync queue.',
    cancel: 'Cancel',
    actions: {
      'fpni-carryover-backfill': {
        description: 'Backfill missing FP-NI carryover (session materials + finish_product_added history)',
        summaryYieldBefore: 'Yield variance before (%)',
        summaryYieldAfter: 'Yield variance after (%)',
        summaryYieldDelta: 'Yield delta (%)',
        guardCarryoverPresent: 'Carryover PSM already present',
        guardHistoryPresent: 'finish_product_added history already present',
      },
      'fpni-material-allocation-repair': {
        description: 'FP-NI locked session material repair',
      },
      'release-stranded-output': {
        description: 'Release stranded OUTPUT production summaries (is_approved N → NULL) and reverse-sync to Oracle',
        paramProductionNumbers: 'Exact OUTPUT production_number values (JSON array)',
      },
      'untie-summary-mtime': {
        description: 'Re-stamp INPUT production_summary m_time from session Eastern start (untie VB6 sort collision)',
        paramProductionNumber: 'Day-input production_number of the INPUT summary',
        paramExpectedMTime: 'Current m_time (HHMMSS) — must match before apply',
      },
    },
    previewKinds: {
      preview_remove: 'Material to remove',
      preview_keep: 'Material to keep',
      preview_add: 'Material to add',
      preview_lot_return: 'Weight returned to lot',
      preview_lot_status: 'Lot status change',
      preview_lot_balance: 'Lot balance change',
      preview_targets: 'Recomputed targets',
    },
    previewSummaryTitle: 'Repair preview summary',
  },
  sync: {
    cloudOffline: 'Cloud offline',
    synced: 'Cloud synced',
    pending: 'pending',
    issues: 'sync issue(s)',
    syncing: 'Syncing...',
    lastPull: 'Last pull',
    neverPulled: 'Never pulled',
    failed: 'failed',
    pullFailed: 'Pull failed',
    pullStale: 'Pull stale',
    offlineWorkNotConfirmed: 'Offline work not confirmed on the cloud',
    offlineWorkNotConfirmedDetail: 'Changes made while the internet was down have not been confirmed on the cloud yet. Contact support before re-entering any data.',
  },
  offline: {
    youAreOffline: 'You are offline — actions will be saved locally',
    backOnline: 'Back online',
    pendingActions: 'pending action(s)',
    syncing: 'Syncing...',
    allSynced: 'All actions synced',
    offlineLabel: 'Offline',
  },

  // Tutorial / Walkthrough
  tutorial: {
    skip: 'Skip Tour',
    pageTitle: 'Tutorial',
    pageSubtitle: 'Learn how to use the production system with step-by-step guides.',
    replayTour: 'Replay Tour',
    completed: 'Training completed',
    tourInfo: 'The interactive tour has {{count}} steps and will guide you through the key features for your role.',

    // Tutorial page sections
    sections: {
      dailyQueue: 'Daily Queue',
      dailyQueueDesc: 'View your assigned production sessions for today. Each session card shows the product, station, and target output.',
      weeklySchedule: 'Weekly Schedule',
      weeklyScheduleDesc: 'See the full week schedule at a glance. Plan ahead and know what sessions are coming up.',
      production: 'Production',
      productionDesc: 'Clock into a station, start production sessions, scan boxes, track waste, and complete sessions.',
      supervisorDashboard: 'Supervisor Dashboard',
      supervisorDashboardDesc: 'Monitor all active stations, see real-time progress, and manage production from one screen.',
      productionPlanning: 'Production Planning',
      productionPlanningDesc: 'Create and schedule production sessions. Assign products, stations, operators, and target weights.',
      schedule: 'Schedule',
      scheduleDesc: 'View the full production schedule across all stations in daily or weekly format.',
      transfers: 'Warehouse Transfers',
      transfersDesc: 'Manage warehouse transfers — scan boxes in and out of cold storage and track transfer status.',
      inventory: 'Inventory',
      inventoryDesc: 'View lot tracking, finished products, box lookup, and packing materials.',
      reports: 'Reports',
      reportsDesc: 'Access end-of-day summaries, yield variance reports, and override history.',
      productionRecords: 'Production Records',
      productionRecordsDesc: 'Search and review historical production session data.',
      managerDashboard: 'Manager Dashboard',
      managerDashboardDesc: 'Review pending approvals, transfer requests, and production status.',
      adminDashboard: 'Admin Dashboard',
      adminDashboardDesc: 'Overview of system activity, user statistics, and key metrics.',
      userManagement: 'User Management',
      userManagementDesc: 'View and manage all user accounts. Assign roles and monitor login activity.',
      stationManagement: 'Station Management',
      stationManagementDesc: 'Create, edit, and configure production stations. Set allowed materials and locations.',
      settings: 'Settings',
      settingsDesc: 'Configure system preferences, manage ICS user accounts, and view system information.',
    },

    // Operator tour steps
    operator: {
      dailyQueue: { title: 'Daily Queue', content: 'This is your daily work queue. You\'ll see all sessions assigned to you today.' },
      sessionCard: { title: 'Session Card', content: 'Each card is a production session. Tap to see details like product, station, and target weight.' },
      sessionCardScanIn: { title: 'Clock In Modal', content: 'This opens when you tap a session card. You can see session details, who\'s assigned, and scan your badge or enter your employee number to clock in.' },
      weeklyView: { title: 'Weekly Schedule', content: 'Switch here to see your full week schedule at a glance.' },
      clockIn: { title: 'Scan In', content: 'Scan a station QR code or select your station to scan in and start working.' },
      scanBadge: { title: 'Scan Badge', content: 'After scanning a station, you\'ll see the session info. Now scan your employee badge to clock in.' },
      scanSuccess: { title: 'Scanned In!', content: 'You\'re now clocked in and will be redirected to your production view. You can start working!' },
      sessionInfo: { title: 'Session Info', content: 'This bar shows your current session details — product, raw material, lot number, packing specs, and expected boxes.' },
      startSession: { title: 'Start Session', content: 'Once assigned to a session, tap Start to begin production.' },
      boxScanning: { title: 'Box Scanning', content: 'Enter or weigh each box here. When the weight is within the target range, it turns green and you can print the label.' },
      printLabel: { title: 'Print Label', content: 'When the weight is valid, tap Print Label to print a barcode label for the box. The label includes product, lot, weight, and date code.' },
      badgeLabelScan: { title: 'Badge & Label Scan', content: 'After printing, scan your badge then scan the label on the box to confirm it\'s applied. This ensures every box is properly labeled.' },
      finishSession: { title: 'Finish Session', content: 'When you\'ve packed all target boxes, the Target Reached screen appears. Tap Complete Session to proceed to waste tracking.' },
      wasteTracking: { title: 'Waste Tracking', content: 'Record all waste and byproducts step by step — disposable waste, human error, by-products, and remainders. A supervisor will review and sign off at the end.' },
      completeSession: { title: 'Complete Session', content: 'When you\'re done, tap Complete. You\'ll record waste and byproducts, then a supervisor may need to approve if yield is outside range.' },
    },

    // Supervisor tour steps
    supervisor: {
      dashboard: { title: 'Supervisor Dashboard', content: 'This is your dashboard. See all active stations and their current status at a glance.' },
      stationCards: { title: 'Station Cards', content: 'Each card shows a station\'s current session, operator, and production progress.' },
      productionPlanning: { title: 'Production Planning', content: 'Create new production sessions here. Assign product, station, operator, and target weight.' },
      sessionList: { title: 'Session List', content: 'View all planned, active, and completed sessions. Reorder the queue as needed.' },
      schedule: { title: 'Schedule', content: 'See the full production schedule across all stations.' },
      transfers: { title: 'Warehouse Transfers', content: 'Manage warehouse transfers — scan boxes in and out of cold storage.' },
      inventory: { title: 'Inventory', content: 'View lot tracking, finished products, and packing materials.' },
      reports: { title: 'Reports', content: 'Access all production reports from this page.' },
      reportEod: { title: 'End of Day Report', content: 'Review the daily production summary — output, yield, and operator performance.' },
      reportYield: { title: 'Yield Variance', content: 'Sessions flagged for yield review appear here. Approve or investigate as needed.' },
      productionRecords: { title: 'Production Records', content: 'Search and review historical production data.' },
      settings: { title: 'Settings', content: 'Configure your preferences and system settings.' },
    },

    // Manager tour steps
    manager: {
      dashboard: { title: 'Manager Dashboard', content: 'Your approval queue and production overview. Pending items appear here.' },
      approvalCards: { title: 'Approval Queue', content: 'Review and approve or reject transfer requests and other manager-level actions.' },
      productionPlanning: { title: 'Production Planning', content: 'View and create production sessions, same as supervisor access.' },
      schedule: { title: 'Schedule', content: 'Full production schedule across all stations and dates.' },
      transfers: { title: 'Warehouse Transfers', content: 'Manage warehouse transfers and cold storage movements.' },
      inventory: { title: 'Inventory', content: 'Track lots, finished products, and material consumption.' },
      reports: { title: 'Reports', content: 'Access production reports — end of day, yield variance, and overrides.' },
      productionRecords: { title: 'Production Records', content: 'Search past production sessions and performance data.' },
    },

    // Admin tour steps
    admin: {
      dashboard: { title: 'Admin Dashboard', content: 'System overview with user statistics and key metrics.' },
      users: { title: 'User Management', content: 'View and manage all user accounts in the system.' },
      inventory: { title: 'Inventory', content: 'Full inventory management — lots, finished products, and materials.' },
      stations: { title: 'Station Management', content: 'Create, edit, and configure production stations.' },
      schedule: { title: 'Schedule', content: 'View the production schedule across all stations.' },
      settings: { title: 'Settings', content: 'Configure system preferences and manage ICS user accounts.' },
      icsUsers: { title: 'ICS User Management', content: 'Enable ICS employees as production system users. Set roles and temporary passwords.' },
    },
  },
  // Manager Guide (step-by-step workflow)
  managerGuide: {
    title: 'Production Guide',
    next: 'Next Step',
    previous: 'Previous',
    steps: {
      plan: 'Plan',
      transfers: 'Transfers',
      production: 'Production',
      review: 'Review',
    },
    plan: {
      sessionsToday: 'Sessions Today',
      sessionsOnDate: 'Sessions on {{date}}',
      planned: 'submitted',
      active: 'active',
      completed: 'completed',
      operators: 'Operators',
      packers: 'Packers',
      stations: 'Stations',
      scheduledToday: 'scheduled today',
      scheduledOnDate: 'scheduled on {{date}}',
      todaysSessions: "Today's Sessions",
      sessionsForDate: 'Sessions for {{date}}',
      createSession: 'Create Session',
      viewAll: 'View All',
      noSessions: 'No sessions planned for today',
      createFirst: 'Create your first session',
      sessionCreated: 'Session created successfully',
      cancelled: 'Cancelled',
    },
    transfers: {
      title: 'Transfers',
      thisWeek: 'This Week',
      pending: 'Pending',
      scanned: 'Scanned',
      completed: 'Completed',
      recentTransfers: 'Recent Transfers',
      last7Days: 'last 7 days',
      viewAll: 'View All Transfers',
      noTransfers: 'No transfers in the last 7 days',
      transferNo: 'Transfer #',
      date: 'Date',
      from: 'From',
      to: 'To',
      items: 'Items',
      status: 'Status',
      lastEdited: 'Last Edited',
      showVb6: 'Show VB6 Transfers',
    },
    produce: {
      activeStations: 'Active Stations',
      boxesPacked: 'Boxes Packed',
      expectedOutput: 'Expected Output',
      actualOutput: 'Actual Output',
      totalWeight: 'Total Weight',
      waitingToStart: 'waiting to start',
      openStations: 'Open Stations',
      allStarted: 'All sessions started',
      promoteFailed: 'Failed to open stations',
      liveProgress: 'Live Progress',
      fullView: 'Full View',
      noActive: 'No active sessions right now',
    },
    review: {
      totalOutput: 'Total Output',
      of: 'of',
      expected: 'expected',
      completion: 'Completion',
      sessionsCompleted: 'Sessions Completed',
      pendingApprovals: 'Pending Approvals',
      approvalsTitle: 'Items Awaiting Approval',
      quickLinks: 'Quick Links',
      endOfDay: 'End of Day Summary',
      endOfDayDesc: 'View daily production report',
      records: 'Production Records',
      recordsDesc: 'Browse historical records',
      transfers: 'Transfers',
      transfersDesc: 'View & manage transfers',
      // Compact end-of-day report
      productionSummary: 'Production Summary',
      materialFlow: 'Material Flow',
      input: 'Input',
      output: 'Output',
      waste: 'Waste',
      returns: 'Returns & By-Products',
      utilization: 'Utilization',
      byProduct: 'By Product',
      product: 'Product',
      sessions: 'Sessions',
      boxes: 'Boxes',
      weight: 'Weight',
      yield: 'Yield',
      wasteBreakdown: 'Waste Breakdown',
      wasteType: 'Type',
      alerts: 'Alerts',
      noData: 'No production data for today',
      viewFullReport: 'View Full Report',
      complete: 'Complete',
    },
  },

  // Freshness indicators for by-product lots
  freshness: {
    fresh: 'Fresh',
    useSoon: 'Use Soon',
    oldStock: 'Old Stock',
    alertByProductAging: 'By-product needs to be used in production',
    resolveForToday: 'Resolve for Today',
  },
  // WCO-2618: per-row (i) print-audit popover on the Print Labels sections.
  printAudit: {
    title: 'Label print history',
    empty: 'No labels printed yet.',
    printedColumn: 'Printed',
    printed: 'Printed',
    notPrinted: 'Not printed',
    by: 'by',
    statusActive: 'Active',
    statusSuperseded: 'Superseded',
    statusVoided: 'Voided',
  },
} as const;
